firebird.factory('avDeviceService', [
    '$rootScope',
    '$window',
    '$q',
    '$sce',
    '$interval',
    '$timeout',
    'logService',
    'storageService',
    'helperService',
    'localStreamManagerService',
    'socketService',
    'supportLinkService',
    'meetingService',
    'localizationService',
    'brandingService',
    'attendeeService',
    'connectivityService',
    'growlService',
    'executionEnvironmentEnum',
    'notificationService',
    'macSecurityPreferencesService',
    'configService',
    'avDeviceSettingsService',
    'mediaDeviceService',
    function (
        $rootScope,
        $window,
        $q,
        $sce,
        $interval,
        $timeout,
        logService,
        storageService,
        helperService,
        localStreamManagerService,
        socketService,
        supportLinkService,
        meetingService,
        localizationService,
        brandingService,
        attendeeService,
        connectivityService,
        growlService,
        executionEnvironmentEnum,
        notificationService,
        macSecurityPreferencesService,
        configService,
        avDeviceSettingsService,
        mediaDeviceService
    ) {
        const logger = logService.createLoggerWithSource('avDeviceService');

        var devicesAvailable = {
            microphone: false,
            speaker: false,
            webcam: false
        };

        var devicePermissions = {
            audioAllowed: false,
            videoAllowed: false
        };

        var macOSSystemPermissions = {
            audioAllowed: false,
            videoAllowed: false
        };

        var deviceErrors = {
            camError: false,
            micError: false,
            micAdditional: '',
            camAdditional: ''
        };

        //New Device Error handling logic...
        //leaving original "deviceErrors" alone for webinar compatibility
        var sfuDeviceErrors = {};

        var ignoreCamera = false;

        var devicePermissionPromptTimeout = null;

        var pendingDeviceReconnect = false;

        var isPermissionOverlayOpen = false;

        var microphoneDeviceSwitching = false;

        var audioDisconnected = false;

        var micSystemDeviceObj = null;
        var micCommunicationsDeviceObj = null;

        let newVideoDeviceConnected = false; // the only webcam was plugged in during the meeting

        var mediaDevices = null;

        var service = {
            microphones: [],
            speakers: [],
            webcams: [],

            oldMicrophones: {},
            oldSpeakers: {},
            oldWebcams: {},

            defaultAudioInputObj: null,
            defaultAudioOutputObj: null,

            ignoredDevices: {
                microphones: {
                    communications: null,
                    default: null
                },
                webcams: {
                    communications: null,
                    default: null
                },
                speakers: {
                    communications: null,
                    default: null
                }
            },

            audioContext: null,
            micLevelIntervals: [],
            micSilenceDetectionInterval: null,

            deviceTypes: {
                AUDIO_INPUT: 1,
                AUDIO_OUTPUT: 2,
                VIDEO_INPUT: 3
            },

            deviceChangeConfig: {
                inProgress: null,
                isPending: null,
                tryReconnect: true,
                delay: 2000,
                timeout: 5000,
                delayTimer: null,
                webcam: true, // include webcam
                // webcam* are only used in case of VDI (citrix|vmware)
                webcamDelay: 2000,
                webcamTimer: null
            },

            badMicDetectConfig: {
                enabled: false,
                visible: false,
                value: 'stats', // 'freq' or 'level'
                freqThreshold: 5, // Threshold for freq level
                micLevelThreshold: 0.05, // Threshold for mic level
                silenceDuration: 500, // Detect silence for this duration in ms
                startDelay: 500, // Detection start delay
                delayTimer: null, // Detection start delay timer
                maxRetries: 3, // Maximum number of retries for detection
                promptZero: true,
                retry: 1, // Current retry
                timeoutDuration: 2000, // Default timeout duration (2 seconds)
                intervalDuration: 200, // Default interval duration (200 milliseconds)
                micLevelInterval: null,
                statsThreshold: 0.01, // Threshold for stats audio level
                audioLevels: [], // Captured audio levels for intervals
                maxLogs: 20, // Captured max log entries for audio levels
                newStream: true, // mark false once detection is done for device change
                eventCount: 0,
                problemCount: 0,
                maxCount: 3,
                detectStats: false, // Start detecting stats, will set to true once unmuted
                hideAlertAfter: 5000 // Problem alert hide
            },

            // #ANYAM-111616: Fix webcam light for permission check
            usePermissionsAPI: true,

            // #ANYAM-115003: Fix duplicate devices in list
            detectDuplicateDevice: true,

            updateConfigValues: updateConfigValues,
            enumerateDevices: enumerateDevices,
            enumerateDevicesWithRetries: enumerateDevicesWithRetries,
            clearDevices: clearDevices,
            getStream: getStream,
            generateConstraints: generateConstraints,
            generateDeviceObj: generateDeviceObj,
            isRecommendedDevice: isRecommendedDevice,
            isSystemDevice: isSystemDevice,
            isDeviceInUse: isDeviceInUse,
            addDeviceToArray: addDeviceToArray,
            getLabelForDeviceId: getLabelForDeviceId,
            checkIfDeviceIsAvailable: checkIfDeviceIsAvailable,
            getCurrentlySelectedMic: getCurrentlySelectedMic,
            getCurrentlySelectedWebcam: getCurrentlySelectedWebcam,
            checkForBTHeadset: checkForBTHeadset,

            //Mic Level Tracking
            openAudioContext: openAudioContext,
            closeAudioContext: closeAudioContext,
            isAudioTrackActive: isAudioTrackActive,
            createAudioAnalyser: createAudioAnalyser,
            getAverageAudioVolume: getAverageAudioVolume,
            trackMicLevelForPreview: trackMicLevelForPreview,
            stopTrackMicLevelForPreview: stopTrackMicLevelForPreview,
            trackMicLevel: trackMicLevel,
            stopMicLevelTrackingForPreview: stopMicLevelTrackingForPreview,
            canTrackStartStopTalking: canTrackStartStopTalking,
            trackStartStopTalking: trackStartStopTalking,
            cancelNotTalkingTimeout: cancelNotTalkingTimeout,
            untrackStartStopTalking: untrackStartStopTalking,
            getAudioOutputTestSource: getAudioOutputTestSource,
            checkForBadMic: checkForBadMic,
            detectBadMicWithStats: detectBadMicWithStats,

            //Device Availability & Permissions
            setHasMicrophone: setHasMicrophone,
            setHasCamera: setHasCamera,
            hasMicrophone: hasMicrophone,
            hasSpeaker: hasSpeaker,
            hasCamera: hasCamera,
            getDevicePermissions: getDevicePermissions,
            setDevicePermissions: setDevicePermissions,
            sendDevicePermissions: sendDevicePermissions,
            getDeviceErrors: getDeviceErrors,
            setDeviceErrors: setDeviceErrors,
            setShouldIgnoreCamera: setShouldIgnoreCamera,
            shouldIgnoreCamera: shouldIgnoreCamera,
            getActiveDevices: getActiveDevices,
            getDeviceLabelFromId: getDeviceLabelFromId,

            hasMicrophoneDeviceError: hasMicrophoneDeviceError,
            isMicrophoneHavingAnIssue: isMicrophoneHavingAnIssue,

            isAudioDeviceBlocked: isAudioDeviceBlocked,
            isVideoDeviceBlocked: isVideoDeviceBlocked,

            isWebcamHavingAnIssue: isWebcamHavingAnIssue,
            hasWebcamDeviceError: hasWebcamDeviceError,

            getIsMicrophoneDeviceSwitching: getIsMicrophoneDeviceSwitching,
            setIsMicrophoneDeviceSwitching: setIsMicrophoneDeviceSwitching,

            getIsNewVideoDeviceConnected: getIsNewVideoDeviceConnected,
            setIsNewVideoDeviceConnected: setIsNewVideoDeviceConnected,

            checkIfAVDevicePermissionsGranted: checkIfAVDevicePermissionsGranted,
            showBlockedPermissionsBanner: showBlockedPermissionsBanner,
            hideBlockedPermissionsBanner: hideBlockedPermissionsBanner,
            showBlockedAlert: showBlockedAlert,
            determineMicAndCamAvailability: determineMicAndCamAvailability,
            showDevicePermissionPromptWithDelay: showDevicePermissionPromptWithDelay,
            cancelDevicePermissionPrompt: cancelDevicePermissionPrompt,

            initDeviceChangeListener: initDeviceChangeListener,
            removeDeviceChangeListener: removeDeviceChangeListener,
            onDeviceChange: onDeviceChange,
            handlePendingOnDeviceChangeIfAny: handlePendingOnDeviceChangeIfAny,
            promptForExplicitlyChosenDevices: promptForExplicitlyChosenDevices,
            setIsPendingDeviceReconnect: setIsPendingDeviceReconnect,
            getIsPendingDeviceReconnect: getIsPendingDeviceReconnect,
            requestSystemPermissionForDevices: requestSystemPermissionForDevices,
            getMacOSSystemPermissions: getMacOSSystemPermissions,

            //New Device Error logic - For SFU (Non-Webinar)
            setSfuDeviceError: setSfuDeviceError,
            clearSfuDeviceError: clearSfuDeviceError,
            getSfuDeviceErrorFromId: getSfuDeviceErrorFromId,
            getSfuDeviceErrors: getSfuDeviceErrors,

            startDevicePermissionsFlow: startDevicePermissionsFlow,
            audioOrVideoBlocked: audioOrVideoBlocked,
            startWebrtcPromptOverlay: startWebrtcPromptOverlay,

            //platform specific functions
            openMicCamPermissionTroubleShootPage: openMicCamPermissionTroubleShootPage,
            openMicCamPermissionTroubleShootPageForElectron: openMicCamPermissionTroubleShootPageForElectron,
            openMicrophoneSystemPreference: openMicrophoneSystemPreference,
            openCameraSystemPreference: openCameraSystemPreference,

            setIsPermissionOverlayOpen: setIsPermissionOverlayOpen,
            getIsPermissionOverlayOpen: getIsPermissionOverlayOpen,

            isAudioDisconnected: isAudioDisconnected,
            toggleMuteAudioInputAndOutput: toggleMuteAudioInputAndOutput,
            setAudioDisconnected: setAudioDisconnected,
            handleDetachedDeviceTrackOnVDI: handleDetachedDeviceTrackOnVDI,
            switchToSelectedAudioInputOnVdi: switchToSelectedAudioInputOnVdi,

            getAllPhysicalMicDevices: getAllPhysicalMicDevices
        };

        return service;

        ////////////////////////

        function updateConfigValues() {
            const fixBadMic = configService.getValue('fixBadMic');
            if (fixBadMic && fixBadMic !== 'false') {
                service.badMicDetectConfig.enabled = true;
                service.badMicDetectConfig.visible = true;
                configService.setConfigObjectFromName(service.badMicDetectConfig, 'fixBadMic');
                if (fixBadMic == 'freq' || fixBadMic == 'level') {
                    service.badMicDetectConfig.value = fixBadMic;
                } else {
                    service.badMicDetectConfig.value = 'stats';
                }
            } else {
                service.badMicDetectConfig.enabled = false;
                service.badMicDetectConfig.visible = false;
            }

            service.detectDuplicateDevice = configService.getValue('detectDuplicateDevice') !== 'false';
        }

        function sendMsgflowEvent(name, data) {
            $rootScope.$broadcast('send-msgflow-event', { name, data });
        }

        function updateEnumerateDevicesDelay(delay, reason) {
            $rootScope.$broadcast('update-device-delay', { event: 'dev-list', delay, reason });
        }

        function shouldRetryEnumerateDevices() {
            if (!meetingService.getIsSfuEnabled()) {
                return false;
            }
            let config = $window.Janus.retryDeviceOnTimeout;
            if (!config.initialized) {
                configService.readStorage(meetingService.getMeetingCode());
                configService.setConfigObjectFromName($window.Janus.retryDeviceOnTimeout, 'retryDeviceOnTimeout', {
                    default: true
                });
                $window.Janus.retryDeviceOnTimeout.initialized = true;
                config = $window.Janus.retryDeviceOnTimeout;
            }
            return config.enabled && config.list;
        }

        function enumerateDevices(reason) {
            var deferred = $q.defer();

            if (
                $window.navigator &&
                $window.navigator.mediaDevices &&
                $window.navigator.mediaDevices.enumerateDevices
            ) {
                logger.info('enumerateDevices', 'Attempting to enumerate devices', reason);
                const startTime = Date.now();
                const retryEnumerateDevices = shouldRetryEnumerateDevices();

                const enumerateDevicesPromise = retryEnumerateDevices
                    ? service.enumerateDevicesWithRetries(reason)
                    : $window.navigator.mediaDevices.enumerateDevices();
                enumerateDevicesPromise
                    .then(function (devices) {
                        const devicesString = devices.map(d => `[${d.kind}:${d.label}]`).join(',');
                        logger.info('#enumerateDevices: Successfully enumerated devices', devicesString, devices);
                        const delay = Date.now() - startTime;
                        updateEnumerateDevicesDelay(delay, reason + ':success');
                        //Clear device lists before populating them with updated devices
                        service.clearDevices();

                        devices.forEach(function (device, index, array) {
                            if (device.label.includes('(Virtual)')) {
                                return;
                            }
                            switch (device.kind) {
                                case 'audioinput':
                                    service.addDeviceToArray(device, service.microphones);
                                    break;
                                case 'audiooutput':
                                    service.addDeviceToArray(device, service.speakers);
                                    break;
                                case 'videoinput':
                                    service.addDeviceToArray(device, service.webcams);
                                    if (helperService.getDevicePlatform() === 'iOS' && device.label === 'Back Camera') {
                                        logger.debug('enumerateDevices', 'Skipping Back Camera...');
                                        break;
                                    }
                                    break;
                                default:
                                    logger.error('enumerateDevices', 'Unhandled device type', device);
                                    break;
                            }
                        });

                        //Device Selection Preview will run its own BT Headset filtering
                        if (!meetingService.shouldShowDeviceSelectionPreview()) {
                            try {
                                service.checkForBTHeadset();
                            } catch (e) {
                                logger.error('enumerateDevices', 'Error checking for BT headset and headphones', e);
                            }
                        }

                        // prioritize 'communications' or 'default' first, then used
                        service.microphones.sort(compareDevices);
                        service.speakers.sort(compareDevices);
                        updateOriginalDeviceIds(service.microphones);
                        updateOriginalDeviceIds(service.speakers);
                        service.webcams.sort(compareDevices);

                        devicesAvailable.microphone = service.microphones.length > 0;
                        devicesAvailable.speaker = service.speakers.length > 0;
                        devicesAvailable.webcam = service.webcams.length > 0;

                        deferred.resolve();
                    })
                    .catch(function (err) {
                        const delay = Date.now() - startTime;
                        updateEnumerateDevicesDelay(delay, reason + ':error');
                        logger.warn('enumerateDevices', 'Error enumerating devices', err);
                        deferred.reject(err);
                    });
            } else {
                deferred.reject('enumerateDevices not available');
            }

            return deferred.promise;
        }

        function enumerateDevicesWithRetries(reason) {
            const startTime = Date.now();
            const config = $window.Janus.retryDeviceOnTimeout;
            const maxAttempts = config.attempts;
            const initialTimeout = config.timeout;
            const backoffMultiplier = config.backoff;

            return new Promise((resolve, reject) => {
                function tryEnumerate(attemptNum, timeout) {
                    let timeoutId;

                    if (attemptNum >= maxAttempts) {
                        const totalDuration = Date.now() - startTime;
                        logger.error(
                            'enumerateDevicesWithRetries',
                            `Failed after ${maxAttempts} attempts in ${totalDuration}ms`
                        );
                        sendMsgflowEvent('dev-list:fail', {
                            reason,
                            attempt: attemptNum,
                            duration: totalDuration
                        });
                        reject({ name: 'Timeout', message: 'Failed to enumerate devices' });
                        return;
                    }

                    logger.info('enumerateDevicesWithRetries', `Attempt=${attemptNum}, timeout=${timeout}ms`);

                    timeoutId = setTimeout(() => {
                        timeoutId = null;
                        sendMsgflowEvent('dev-list:timeout', {
                            reason,
                            timeout,
                            attempt: attemptNum
                        });
                        tryEnumerate(attemptNum + 1, timeout * backoffMultiplier);
                    }, timeout);

                    const enumerateStartTime = Date.now();
                    $window.navigator.mediaDevices
                        .enumerateDevices()
                        .then(devices => {
                            const enumerateDuration = Date.now() - enumerateStartTime;
                            const totalDuration = Date.now() - startTime;

                            if (timeoutId) {
                                clearTimeout(timeoutId);
                                timeoutId = null;

                                logger.info(
                                    'enumerateDevicesWithRetries',
                                    `Success on attempt ${attemptNum} in ${enumerateDuration}ms, total ${totalDuration}ms`
                                );
                                sendMsgflowEvent('dev-list:succ', {
                                    reason,
                                    attempt: attemptNum,
                                    duration: enumerateDuration,
                                    total: totalDuration
                                });
                                resolve(devices);
                            } else {
                                logger.warn(
                                    'enumerateDevicesWithRetries',
                                    `Late success on attempt ${attemptNum}, already timed out`
                                );
                                sendMsgflowEvent('dev-list:late-succ', {
                                    reason,
                                    attempt: attemptNum,
                                    duration: enumerateDuration,
                                    total: totalDuration
                                });
                            }
                        })
                        .catch(error => {
                            logger.warn(
                                'enumerateDevicesWithRetries',
                                `Attempt ${attemptNum} failed with error`,
                                error
                            );
                            if (timeoutId) {
                                clearTimeout(timeoutId);
                                timeoutId = null;
                            }
                        });
                }

                tryEnumerate(0, initialTimeout);
            });
        }

        function cloneDeviceListToMap(deviceList, deviceMap) {
            deviceList.reduce((acc, device) => {
                acc[device.deviceId] = device;
                return acc;
            }, deviceMap);
        }

        function clearDevices() {
            service.oldMicrophones = {};
            service.oldSpeakers = {};
            service.oldWebcams = {};
            cloneDeviceListToMap(service.microphones, service.oldMicrophones);
            cloneDeviceListToMap(service.speakers, service.oldSpeakers);
            cloneDeviceListToMap(service.webcams, service.oldWebcams);

            service.microphones = [];
            service.speakers = [];
            service.webcams = [];

            service.ignoredDevices.microphones.communications = null;
            service.ignoredDevices.microphones.default = null;

            service.ignoredDevices.webcams.communications = null;
            service.ignoredDevices.webcams.default = null;

            service.ignoredDevices.speakers.communications = null;
            service.ignoredDevices.speakers.default = null;
        }

        function updateOriginalDeviceIds(deviceList) {
            const communicationDevice = deviceList.find(d => d.deviceId === 'communications');
            if (communicationDevice) {
                const originalDevice = deviceList.find(device => device.groupId === communicationDevice.groupId);
                if (originalDevice) {
                    communicationDevice.originalId = originalDevice.deviceId;
                }
            }
        }

        function getDeviceLabelFromId(deviceId) {
            const deviceList = service.microphones.concat(service.speakers).concat(service.webcams);
            const device = deviceList.find(device => device.deviceId === deviceId);
            return device ? device.originalLabel || device.label : '';
        }

        function getStream(constraints, noStream) {
            var deferred = $q.defer();

            if (noStream) {
                //Resolve without attempting to retrieve stream
                deferred.resolve(null);
                return deferred.promise;
            }

            if ($window.navigator && $window.navigator.getUserMedia) {
                const deviceId =
                    getDeviceIdFromConstraints(constraints, 'audio') ||
                    getDeviceIdFromConstraints(constraints, 'video');

                var getUserMediaTimeout = $timeout(function () {
                    if (deviceId) {
                        logger.debug('getStream', `avDeviceService - getUserMedia timed out for: ${deviceId}`);
                    } else {
                        logger.debug('getStream', 'avDeviceService - getUserMedia timed out');
                    }
                }, 3000);

                // if (!mediaDevices) {
                //     setMediaDevice();
                // }
                // Revert useMediaService code, Ref. ANYAM-114891
                $window.navigator.mediaDevices
                    .getUserMedia(constraints)
                    .then(stream => {
                        $timeout.cancel(getUserMediaTimeout);
                        deferred.resolve(stream);
                    })
                    .catch(err => {
                        $timeout.cancel(getUserMediaTimeout);
                        deferred.reject(err);
                        logger.error('getStream', `getUserMedia error for deviceId=${deviceId}`, err);
                        sendMsgflowEvent('device-error', {
                            reason: 'getStream',
                            constraints: constraints,
                            error: err?.name
                        });
                    });
            } else {
                deferred.reject('getUserMedia not available');
            }

            return deferred.promise;
        }

        function getDeviceIdFromConstraints(constraints, type) {
            if (!constraints || !constraints[type]) {
                return null;
            }

            const deviceId =
                constraints[type]?.deviceId?.exact ||
                constraints[type]?.deviceId ||
                constraints[type]?.mandatory?.deviceId?.exact ||
                constraints[type]?.mandatory?.deviceId ||
                constraints[type]?.mandatory?.sourceId;

            return deviceId || '';
        }

        function generateConstraints(type, srcId, videoType) {
            var constraints = { audio: false, video: false };

            if (type === 'audio') {
                constraints.audio = {
                    deviceId: srcId ? { exact: srcId } : undefined
                };
            }

            if (type === 'video') {
                constraints.video = localStreamManagerService.getIdealVideoConstraints(videoType);
                constraints.video.deviceId = srcId ? { exact: srcId } : undefined;
            }

            return constraints;
        }

        function setMediaDevice() {
            var useMediaService = configService.getValue('useMediaService');
            mediaDevices =
                useMediaService && useMediaService !== 'false' && meetingService.getIsSfuEnabled()
                    ? mediaDeviceService
                    : $window.navigator?.mediaDevices;
        }

        function isRecommendedDevice(deviceObj) {
            if (service.isSystemDevice(deviceObj.deviceInfo.deviceId)) {
                return false;
            }

            const getDevices = kind => {
                switch (kind) {
                    case 'audioinput':
                        return service.microphones;
                    case 'audiooutput':
                        return service.speakers;
                    case 'videoinput':
                        return service.webcams;
                    default:
                        return [];
                }
            };

            return getDevices(deviceObj.deviceInfo.kind).some(
                d => d.groupId === deviceObj.deviceInfo.groupId && service.isSystemDevice(d.deviceId)
            );
        }

        function isSystemDevice(deviceId) {
            return deviceId === 'default' || deviceId === 'communications';
        }

        function setDeviceLabels(deviceObj, defaultLabel) {
            let label;
            if (deviceObj.deviceInfo.label) {
                label = deviceObj.deviceInfo.label.replace(/\s+/g, ' ');
            } else {
                label = defaultLabel;
            }

            const isSystem =
                deviceObj.deviceInfo.deviceId === 'default' || deviceObj.deviceInfo.deviceId === 'communications';

            let displayName, shortDisplayName, fullDisplayName;

            shortDisplayName = label;
            displayName = isSystem ? deviceObj.deviceInfo.originalLabel : label;
            fullDisplayName = isSystem ? `${label} - ${deviceObj.deviceInfo.originalLabel}` : label;

            deviceObj.displayName = displayName;
            deviceObj.shortDisplayName = shortDisplayName;
            deviceObj.fullDisplayName = fullDisplayName;
        }

        function generateDeviceObj(mediaDeviceInfo, deviceIndex, noStream) {
            var deferred = $q.defer();
            var constraintsMediaType;
            var constraints;
            var defaultLabel;

            //Example Output:
            // {
            //     deviceInfo: {
            //         deviceId: 1234,
            //         kind: 'audioinput',
            //         label: 'Logitech Camera'
            //     },
            //     stream: mediaStreamObject,
            //     micLevel: '75%',
            //     inUse: true,
            //     recommended: true,
            //     displayName: 'Logitech Camera (recommended)',
            //     fullDisplayName: 'Logitech Camera (recommended)',
            //     shortDisplayName: 'Logitech Camera (recommended)'
            // }
            // or for System device
            // {
            //     deviceInfo: {
            //         deviceId: 'default',
            //         kind: 'audioinput',
            //         label: 'Logitech Camera'
            //     },
            //     stream: mediaStreamObject,
            //     micLevel: '75%',
            //     inUse: true,
            //     recommended: false,
            //     displayName: 'Logitech Camera',
            //     fullDisplayName: 'Operating System settings - Logitech Camera',
            //     shortDisplayName: 'Operating System settings'
            // }

            var deviceObj = {
                index: deviceIndex,
                deviceInfo: null,
                stream: null,
                micLevel: 0,
                inUse: false,
                recommended: false,
                displayName: '',
                fullDisplayName: '',
                shortDisplayName: ''
            };

            try {
                if (mediaDeviceInfo) {
                    deviceObj.deviceInfo = mediaDeviceInfo;
                    switch (mediaDeviceInfo.kind) {
                        case 'audioinput':
                            defaultLabel = 'Microphone ' + deviceIndex;
                            constraintsMediaType = 'audio';
                            break;
                        case 'audiooutput':
                            defaultLabel = 'Speaker ' + deviceIndex;
                            break;
                        case 'videoinput':
                            defaultLabel = 'Webcam ' + deviceIndex;
                            constraintsMediaType = 'video';
                            break;
                    }

                    deviceObj.inUse = service.isDeviceInUse(mediaDeviceInfo);
                    deviceObj.recommended = service.isRecommendedDevice(deviceObj);

                    setDeviceLabels(deviceObj, defaultLabel);

                    if (deviceObj.deviceInfo.deviceId === 'default' && mediaDeviceInfo.kind === 'audioinput') {
                        micSystemDeviceObj = deviceObj;
                    }

                    if (deviceObj.deviceInfo.deviceId === 'communications' && mediaDeviceInfo.kind === 'audioinput') {
                        micCommunicationsDeviceObj = deviceObj;
                    }

                    if (noStream) {
                        //Resolve without attempting to retrieve stream
                        deferred.resolve(deviceObj);
                    } else if (constraintsMediaType) {
                        //Audio Input and Video Input
                        constraints = service.generateConstraints(constraintsMediaType, mediaDeviceInfo.deviceId);

                        service
                            .getStream(constraints)
                            .then(function (stream) {
                                deviceObj.stream = stream;

                                if (constraintsMediaType === 'audio') {
                                    try {
                                        service.trackMicLevelForPreview(stream, function (newMicLevel) {
                                            deviceObj.micLevel = newMicLevel;
                                            if (
                                                micSystemDeviceObj &&
                                                micSystemDeviceObj.deviceInfo.groupId === deviceObj.deviceInfo.groupId
                                            ) {
                                                micSystemDeviceObj.micLevel = newMicLevel;
                                                micSystemDeviceObj.stream = stream;
                                            }

                                            if (
                                                micCommunicationsDeviceObj &&
                                                micCommunicationsDeviceObj.deviceInfo.groupId ===
                                                    deviceObj.deviceInfo.groupId
                                            ) {
                                                micCommunicationsDeviceObj.micLevel = newMicLevel;
                                                micCommunicationsDeviceObj.stream = stream;
                                            }
                                        });
                                    } catch (e) {
                                        logger.error('generateDeviceObj', 'error while trackMicLevelForPreview', e);
                                    }
                                } else if (constraintsMediaType === 'video') {
                                    //If we find a valid video stream, we can clear video device error...
                                    //
                                    //This doesn't apply to audio, because user needs to reconnect audio to the Janus call
                                    //before we can clear the audio device error
                                    if (service.getSfuDeviceErrorFromId(mediaDeviceInfo.deviceId)) {
                                        service.clearSfuDeviceError(mediaDeviceInfo.deviceId);
                                    }

                                    if (
                                        stream &&
                                        stream.getVideoTracks &&
                                        stream.getVideoTracks()[0] &&
                                        helperService.getVDIRef().getVdiType() !== executionEnvironmentEnum.Citrix
                                    ) {
                                        var targetConstraints = stream.getVideoTracks()[0].getConstraints();

                                        if (targetConstraints) {
                                            targetConstraints.aspectRatio = '1.777777778';
                                        } else {
                                            targetConstraints = { aspectRatio: '1.777777778' };
                                        }
                                        stream.getVideoTracks()[0].applyConstraints(targetConstraints);
                                    }
                                }

                                deferred.resolve(deviceObj);
                            })
                            .catch(function (getStreamErr) {
                                var err = getStreamErr && getStreamErr.message ? getStreamErr.message : getStreamErr;

                                logger.error('generateDeviceObj', 'generateConstraints - getStream error', err);

                                service.setSfuDeviceError(mediaDeviceInfo.deviceId, err);

                                deferred.resolve(deviceObj);
                            });
                    } else {
                        //Audio Output
                        deferred.resolve(deviceObj);
                    }
                } else {
                    deferred.reject('Invalid mediaDeviceInfo');
                }
            } catch (err) {
                deferred.reject(err);
            }

            return deferred.promise;
        }

        function isDeviceInUse(device) {
            if (!device) {
                return false;
            }

            var isInUse = false;

            switch (device.kind) {
                case 'audioinput':
                    isInUse = avDeviceSettingsService.getUsingAudioIn() === device.deviceId;
                    break;
                case 'audiooutput':
                    isInUse = avDeviceSettingsService.getUsingAudioOut() === device.deviceId;
                    break;
                case 'videoinput':
                    isInUse = avDeviceSettingsService.getUsingVideo() === device.deviceId;
                    break;
            }

            return isInUse;
        }

        function addDeviceToArray(deviceToAdd, deviceArray) {
            if (!deviceToAdd || !deviceArray) {
                logger.debug('addDeviceToArray', 'Unable to add device to array due to invalid params');
                return;
            }

            const cloneDevice = d => {
                return {
                    deviceId: d.deviceId,
                    groupId: d.groupId,
                    kind: d.kind,
                    label: d.label,
                    originalLabel: d.label
                };
            };

            if (meetingService.canUseSystemMediaDevice() && service.isSystemDevice(deviceToAdd.deviceId)) {
                const label = localizationService.translate('HEADER.SETTINGS.OS_SETTINGS');

                deviceArray.push({ ...cloneDevice(deviceToAdd), label });

                if (deviceArray.filter(d => d.deviceId === 'default' || d.deviceId === 'communications').length > 1) {
                    // keep only one system device
                    deviceArray.splice(
                        deviceArray.findIndex(e => e.deviceId === 'default'),
                        1
                    );
                }

                return;
            }

            // When a computer has connected devices, the OS sets one mic/webcam as 'default'
            // The enumerated device list will then contain two of the same device -- one will just have the deviceId of 'default'
            // We can safely ignore these as the other device in the list will have the true deviceId
            if (deviceToAdd.deviceId === 'default' || deviceToAdd.deviceId === 'communications') {
                switch (deviceToAdd.kind) {
                    case 'audioinput':
                        service.ignoredDevices.microphones[deviceToAdd.deviceId] = cloneDevice(deviceToAdd);
                        break;
                    case 'audiooutput':
                        service.ignoredDevices.speakers[deviceToAdd.deviceId] = cloneDevice(deviceToAdd);
                        break;
                    case 'videoinput':
                        service.ignoredDevices.webcams[deviceToAdd.deviceId] = cloneDevice(deviceToAdd);
                        break;
                    default:
                        logger.debug('addDeviceToArray', `Invalid device kind: ${deviceToAdd.kind}`);
                        break;
                }

                return;
            }

            // If a device with matching label already exists in the array
            const indexOfMatchingDevice = deviceArray.findIndex(d => d.groupId === deviceToAdd.groupId);
            if (indexOfMatchingDevice > -1 && service.isSystemDevice(deviceArray[indexOfMatchingDevice].deviceId)) {
                // In order to avoid remove 'Default - ' or 'Communication - ' prefix explicity for system devices
                deviceArray[indexOfMatchingDevice].originalLabel = deviceToAdd.label;
            }

            if (service.detectDuplicateDevice) {
                const deviceIndex = deviceArray.findIndex(existingDevice =>
                    deviceToAdd.deviceId && existingDevice.deviceId
                        ? existingDevice.deviceId === deviceToAdd.deviceId
                        : existingDevice.kind === deviceToAdd.kind
                );

                if (deviceIndex >= 0) {
                    deviceArray.splice(deviceIndex, 1);
                    logger.warn('addDeviceToArray', 'Device already exists in the list', deviceToAdd);
                }
            }

            deviceArray.push(cloneDevice(deviceToAdd));
        }

        function formatDeviceLabel(device) {
            return `${device.label} (${device.deviceId.slice(0, 2)}..${device.deviceId.slice(-2)})`;
        }

        function isDeviceListChanged(newList, oldListMap) {
            const newListMap = {};
            cloneDeviceListToMap(newList, newListMap);
            const oldDeviceIds = Object.keys(oldListMap);
            const newDeviceIds = Object.keys(newListMap);
            const newDevices = newDeviceIds
                .filter(deviceId => !oldListMap[deviceId])
                .map(deviceId => formatDeviceLabel(newListMap[deviceId]));
            const removedDevices = oldDeviceIds
                .filter(deviceId => !newListMap[deviceId])
                .map(deviceId => formatDeviceLabel(oldListMap[deviceId]));

            const systemDeviceChanges = {};
            oldDeviceIds.forEach(deviceId => {
                if (service.isSystemDevice(deviceId)) {
                    const oldDevice = oldListMap[deviceId];
                    const newDevice = newListMap[deviceId];
                    if (newDevice && oldDevice.originalId !== newDevice.originalId) {
                        systemDeviceChanges[deviceId] = {
                            old: formatDeviceLabel({ label: oldDevice.originalLabel, deviceId: oldDevice.originalId }),
                            new: formatDeviceLabel({ label: newDevice.originalLabel, deviceId: newDevice.originalId })
                        };
                    }
                }
            });

            const result = {};
            if (newDevices.length > 0) {
                result.added = newDevices;
            }
            if (removedDevices.length > 0) {
                result.removed = removedDevices;
            }
            if (Object.keys(systemDeviceChanges).length > 0) {
                result.updated = systemDeviceChanges;
            }
            return Object.keys(result).length > 0 ? result : false;
        }

        function getLabelForDeviceId(deviceId, kind) {
            let device;
            switch (kind) {
                case 'audioinput':
                    device =
                        service.microphones.find(device => device.deviceId === deviceId) ||
                        service.ignoredDevices.microphones[deviceId];
                    break;
                case 'audiooutput':
                    device =
                        service.speakers.find(device => device.deviceId === deviceId) ||
                        service.ignoredDevices.speakers[deviceId];
                    break;
                case 'videoinput':
                    device =
                        service.webcams.find(device => device.deviceId === deviceId) ||
                        service.ignoredDevices.webcams[deviceId];
                    break;
                default:
                    logger.error('getLabelForDeviceId', `Invalid device kind: ${kind}`);
                    break;
            }
            return device ? device.originalLabel || device.label : '';
        }

        function openAudioContext() {
            var context;

            try {
                if ($window.AudioContext) {
                    context = new $window.AudioContext();
                } else if ($window.webkitAudioContext) {
                    context = new $window.webkitAudioContext();
                }

                if (context) {
                    service.audioContext = context;
                }
            } catch (err) {
                logger.debug('openAudioContext', 'Error creating audio context', err);
            }
        }

        function closeAudioContext() {
            //Ideally we want one AudioContext to track multiple audio sources
            //Only want to close it if both are true:
            //  1. All mic previews are closed (tracked via micLeveLIntervals)
            //  2. We have stopped tracking for locally cloned audio stream (tracked via localStreamManagerService)
            if (
                service.micLevelIntervals.length > 0 ||
                service.micSilenceDetectionInterval ||
                service.isAudioTrackActive(localStreamManagerService.clonedAudioStream)
            ) {
                return;
            }

            if (service.audioContext !== null) {
                service.audioContext.close();
                service.audioContext = null;
            }
        }

        function isAudioTrackActive(stream) {
            if (stream && stream.getAudioTracks) {
                var track = stream.getAudioTracks()[0];
                return track ? track.readyState === 'live' : false;
            }

            return false;
        }

        function createAudioAnalyser() {
            var analyser = null;

            if (service.audioContext) {
                analyser = service.audioContext.createAnalyser();
                analyser.smoothingTimeConstant = 0.3;
                analyser.fftSize = 1024;
            } else {
                logger.debug('createAudioAnalyser', 'Unable to create audioAnalyser - invalid audioContext object');
            }

            return analyser;
        }

        function getAverageAudioVolume(array) {
            var values = 0;
            var average;

            var length = array.length;
            if (!length) {
                return 0;
            }

            // get all the frequency amplitudes
            for (var i = 0; i < length; i++) {
                values += array[i];
            }

            average = values / length;
            return average;
        }

        function stopTrackMicLevelForPreview(trackIndex) {
            if (trackIndex < 0 || trackIndex > service.micLevelIntervals.length - 1) {
                return;
            }

            const micLevelInterval = service.micLevelIntervals[trackIndex];
            if (micLevelInterval) {
                $interval.cancel(micLevelInterval);
            }
        }

        function trackMicLevelForPreview(stream, cb) {
            const micLevelInterval = service.trackMicLevel(stream, cb);
            return service.micLevelIntervals.push(micLevelInterval) - 1;
        }

        function trackMicLevel(stream, cb) {
            if (!helperService.getVDIRef().showAudioInputMicLevelIndicator) {
                return;
            }
            if (!service.audioContext) {
                service.openAudioContext();
            }

            try {
                var micSrc = service.audioContext.createMediaStreamSource(stream);
                var analyser = service.createAudioAnalyser();
                var bufferLength = analyser.frequencyBinCount;
                var dataArray = new Uint8Array(bufferLength);

                micSrc.connect(analyser);
                var micLevelInterval = $interval(function () {
                    analyser.getByteFrequencyData(dataArray);
                    var average = service.getAverageAudioVolume(dataArray);
                    var multiplier = average / 60;
                    var micLevel = 100 * (multiplier > 1 ? 1 : multiplier);

                    if (cb) {
                        cb(micLevel);
                    }
                }, 200);

                return micLevelInterval;
            } catch (e) {
                logger.debug('trackMicLevel', 'Error tracking mic level:', e);
            }
        }

        function checkForBadMic(reason) {
            let config = service.badMicDetectConfig;
            if (!config.enabled) {
                return;
            }
            // On new audio stream ready, reset problem detected flag
            if (reason === 'stream-ready') {
                config.newStream = true;
            }

            const isMuted = attendeeService.getIsAudioMuted();
            if (!isMuted) {
                if (config.value == 'stats') {
                    if (config.newStream) {
                        stopBadMicDetectionWithStats();
                        config.detectStats = true;
                    } else {
                        logger.debug('checkForBadMic', 'Already detected for this mic');
                    }
                } else {
                    stopBadMicDetection();
                    config.delayTimer = $timeout(() => {
                        config.delayTimer = null;
                        startBadMicDetection();
                    }, config.startDelay);
                }
            } else {
                stopBadMicDetection();
                stopBadMicDetectionWithStats();
            }
        }

        function stopBadMicDetection(resetRetries = true) {
            let config = service.badMicDetectConfig;
            if (resetRetries) {
                config.retry = 1;
            }
            if (config.delayTimer) {
                $timeout.cancel(config.delayTimer);
                config.delayTimer = null;
            }

            if (config.micLevelInterval) {
                $interval.cancel(config.micLevelInterval);
                config.micLevelInterval = null;
            }
        }

        function logAudioLevel(ts, level) {
            let config = service.badMicDetectConfig;

            const newEntry = { ts, level };
            config.audioLevels.push(newEntry);

            // Keep only the last maxLogs entries
            if (config.audioLevels.length > config.maxLogs) {
                config.audioLevels.shift();
            }
        }

        // Function to reset the stats config set
        function stopBadMicDetectionWithStats() {
            const config = service.badMicDetectConfig;
            // Reset counts
            config.eventCount = 0;
            config.problemCount = 0;
            // Set detect flag false
            config.detectStats = false;
        }

        // Function to handle volume events
        function detectBadMicWithStats(audioLevel, interval, timestamp) {
            const config = service.badMicDetectConfig;
            logger.log('detectBadMicWithStats', `Audio Level=${audioLevel}, event-count=${config.eventCount}`);

            if (audioLevel > config.statsThreshold) {
                logger.log('detectBadMicWithStats', 'Significant audio detected, No problem in mic');
                config.newStream = false;
                stopBadMicDetectionWithStats();
            } else {
                config.eventCount += 1;
                logAudioLevel(timestamp, audioLevel);
                if (audioLevel > 0) {
                    config.problemCount += 1;
                } else {
                    stopBadMicDetectionWithStats();
                    logger.info('detectBadMicWithStats', 'No audio detected, mic could be muted by hardware switch');
                }

                if (config.eventCount >= config.maxCount && config.problemCount >= config.maxCount) {
                    logger.warn(
                        'detectBadMicWithStats',
                        `Detected bad mic problem, problem-count=${config.problemCount} Audio levels=`,
                        config.audioLevels
                    );
                    if (config.visible) {
                        $rootScope.$broadcast('bad-mic-detected', { hideAlertAfter: config.hideAlertAfter });
                    }
                    sendMsgflowEvent('bad-mic', { interval, timestamp, levels: config.audioLevels });
                    config.newStream = false;
                    stopBadMicDetectionWithStats();
                } else {
                    logger.log(
                        'detectBadMicWithStats',
                        `Continue collecting stats for bad-mic, current stats-count=${config.eventCount}, problem-count=${config.problemCount}`
                    );
                }
            }
        }

        function startBadMicDetection(attempt = 1) {
            if (!helperService.getVDIRef().showAudioInputMicLevelIndicator) {
                return;
            }
            let config = service.badMicDetectConfig;
            if (!config.enabled) {
                return;
            }
            let stream = localStreamManagerService.clonedAudioStream;
            let totalFreq = 0;
            if (!service.audioContext) {
                service.openAudioContext();
            }
            logger.log('startBadMicDetection', 'Detecting bad mic attempt=', attempt);
            try {
                const micSrc = service.audioContext.createMediaStreamSource(stream);
                const analyser = service.createAudioAnalyser();
                const bufferLength = analyser.frequencyBinCount;
                const dataArray = new Uint8Array(bufferLength);
                micSrc.connect(analyser);
                const startTime = Date.now();
                let silenceStart = null;
                const threshold = config.micLevel ? config.micLevelThreshold : config.freqThreshold;

                // Stop if already detecting
                if (config.micLevelInterval) {
                    stopBadMicDetection();
                }
                config.micLevelInterval = $interval(function () {
                    const now = Date.now();
                    if (now - startTime > config.timeoutDuration) {
                        if (totalFreq === 0) {
                            logger.log(
                                'startBadMicDetection',
                                'No activity detected at all, mic could be muted by hardware switch'
                            );
                            if (config.promptZero) {
                                $rootScope.$broadcast('alert-device-in-use', {
                                    device: 'microphone'
                                });
                            }
                        } else {
                            logger.log('startBadMicDetection', 'No bad mic was detected after timeout.');
                        }
                        return stopBadMicDetection();
                    }
                    analyser.getByteFrequencyData(dataArray);
                    let micFreqOrLevel = 0;
                    if (config.micLevel) {
                        var average = service.getAverageAudioVolume(dataArray);
                        var multiplier = average / 60;
                        micFreqOrLevel = 100 * (multiplier > 1 ? 1 : multiplier);
                    } else {
                        micFreqOrLevel = Math.max(...dataArray);
                    }

                    logger.debug(
                        'startBadMicDetection',
                        'Current ' + (config.micLevel ? 'mic' : 'max freq') + ' level = ',
                        micFreqOrLevel
                    );
                    totalFreq = totalFreq + micFreqOrLevel;
                    if (micFreqOrLevel === 0) {
                        logger.debug('startBadMicDetection', 'No activity detected at all, will continue till timeout');
                    } else if (micFreqOrLevel > threshold) {
                        // No activity detected at all or Some activity is detected, mic is working good, hence stopping detection interval
                        silenceStart = null;
                        logger.info(
                            'startBadMicDetection',
                            'Some activity is detected, mic is working good, hence stopping detection interval ',
                            micFreqOrLevel
                        );
                        stopBadMicDetection();
                    } else {
                        if (silenceStart === null) {
                            silenceStart = now;
                        } else if (now - silenceStart >= config.silenceDuration) {
                            logger.debug('startBadMicDetection', 'Very low activity detected');
                            stopBadMicDetection(false);
                            handleBadMicResult();
                        }
                    }
                }, config.intervalDuration);
            } catch (e) {
                logger.debug('startBadMicDetection', 'Error tracking mic level:', e);
            }
        }

        function handleBadMicResult() {
            const config = service.badMicDetectConfig;
            config.retry++;
            if (config.retry > config.maxRetries) {
                config.retry = 1;
                logger.warn('handleBadMicResult', 'Mic has some problem.');
                $rootScope.$broadcast('alert-device-in-use', {
                    device: 'microphone'
                });
            } else {
                startBadMicDetection(config.retry);
            }
        }

        function stopMicLevelTrackingForPreview() {
            service.closeAudioContext();

            service.micLevelIntervals.forEach(function (micLevelInterval, index, array) {
                $interval.cancel(micLevelInterval);

                //After final interval is canceled, clear array
                if (index === array.length - 1) {
                    service.micLevelIntervals = [];
                }
            });
        }

        function canTrackStartStopTalking(stream) {
            if (
                !stream ||
                meetingService.isWebinar() ||
                connectivityService.getAudioThroughPhone() ||
                !service.isAudioTrackActive(stream)
            ) {
                return false;
            }

            return true;
        }

        function trackStartStopTalking(stream, onStartTalkingCallback, onStopTalkingCallback) {
            if (!service.canTrackStartStopTalking(stream)) {
                return;
            }

            logger.debug('trackStartStopTalking', 'Starting local audio tracking');

            stream.onStartTalkingCallback = onStartTalkingCallback;
            stream.onStopTalkingCallback = onStopTalkingCallback;

            if (!stream.localAudioInterval) {
                stream.localAudioInterval = service.trackMicLevel(stream, function (newMicLevel) {
                    // micLevel value can be 0-100

                    // represents hard sounds (words) when talking
                    var talkingThreshold = 30;

                    //Represents the audio levels that drop between words while speaking
                    var softThreshold = 12;

                    if (newMicLevel >= talkingThreshold) {
                        if (
                            stream.previousMicLevel < talkingThreshold &&
                            typeof stream.onStartTalkingCallback === 'function'
                        ) {
                            stream.onStartTalkingCallback();
                        }

                        service.cancelNotTalkingTimeout(stream);
                    } else if (newMicLevel < softThreshold) {
                        if (!stream.notTalkingTimeout) {
                            // if not talking for >0.5s
                            stream.notTalkingTimeout = $timeout(function () {
                                if (
                                    service.canTrackStartStopTalking(stream) &&
                                    typeof stream.onStopTalkingCallback === 'function'
                                ) {
                                    stream.onStopTalkingCallback();
                                }
                            }, 500); // 0.5 second
                        }
                    }

                    stream.previousMicLevel = newMicLevel;
                });
            }
        }

        function cancelNotTalkingTimeout(stream) {
            if (stream && stream.notTalkingTimeout) {
                $timeout.cancel(stream.notTalkingTimeout);
                stream.notTalkingTimeout = null;
            }
        }

        function untrackStartStopTalking(stream) {
            if (!stream) {
                return;
            }

            logger.debug('untrackStartStopTalking', 'Stopping local audio tracking');

            service.cancelNotTalkingTimeout(stream);

            if (stream.localAudioInterval) {
                $interval.cancel(stream.localAudioInterval);
                stream.localAudioInterval = null;
                service.closeAudioContext();
            }

            stream.onStartTalkingCallback = null;
            stream.onStopTalkingCallback = null;
            stream.previousMicLevel = 0;
        }

        function getAudioOutputTestSource() {
            return $sce.trustAsResourceUrl(
                helperService.getLocationProtocol() +
                    '://www.' +
                    helperService.getLocationHost() +
                    '/webconference/sounds/speaker-test.wav'
            ); // 'Generic jazz wav'
        }

        function getActiveDevices() {
            var deferred = $q.defer();

            var activeDevices = {
                audioInput: null,
                videoInput: null,
                audioOutput: null
            };

            service
                .enumerateDevices('get-active-devices')
                .then(function () {
                    if (devicePermissions.audioAllowed && !deviceErrors.micError) {
                        for (var i = 0; i < service.microphones.length; i++) {
                            if (service.microphones[i].deviceId === avDeviceSettingsService.getUsingAudioIn()) {
                                activeDevices.audioInput = service.microphones[i];
                                break;
                            }
                        }
                    }

                    if (devicePermissions.videoAllowed && !deviceErrors.camError) {
                        for (var j = 0; j < service.webcams.length; j++) {
                            if (service.webcams[j].deviceId === avDeviceSettingsService.getUsingVideo()) {
                                activeDevices.videoInput = service.webcams[j];
                                break;
                            }
                        }
                    }

                    for (var k = 0; k < service.speakers.length; k++) {
                        if (service.speakers[k].deviceId === avDeviceSettingsService.getUsingAudioOut()) {
                            activeDevices.audioOutput = service.speakers[k];
                            break;
                        }
                    }

                    deferred.resolve(activeDevices);
                })
                .catch(function (err) {
                    deferred.reject(err);
                });

            return deferred.promise;
        }

        function checkIfDeviceIsAvailable(mediaType, deviceId) {
            var deferred = $q.defer();
            var arrayName;
            var kind;

            switch (mediaType) {
                case service.deviceTypes.AUDIO_INPUT:
                    arrayName = 'microphones';
                    kind = 'audioinput';
                    break;
                case service.deviceTypes.AUDIO_OUTPUT:
                    arrayName = 'speakers';
                    kind = 'audiooutput';
                    break;
                case service.deviceTypes.VIDEO_INPUT:
                    arrayName = 'webcams';
                    kind = 'videoinput';
                    break;
            }

            if (!deviceId) {
                deferred.reject('Invalid deviceId:' + deviceId);
                return deferred.promise;
            }

            if (!arrayName) {
                deferred.reject('Invalid mediaType: ' + mediaType);
                return deferred.promise;
            }

            logger.debug('checkIfDeviceIsAvailable', `Checking if ${kind} device is available: ${deviceId}`);
            service
                .enumerateDevices('check-device-availability')
                .then(function () {
                    var arrayToCheck = service[arrayName];
                    for (var i = 0; i < arrayToCheck.length; i++) {
                        if (arrayToCheck[i].deviceId === deviceId && arrayToCheck[i].kind === kind) {
                            // device is available, but need to update permissions
                            updateDevicesPermissions(kind === 'audioinput', true, kind === 'videoinput', true).then(
                                () => deferred.resolve(true)
                            );

                            return;
                        }
                    }

                    deferred.resolve(false);
                })
                .catch(function (err) {
                    deferred.reject(err);
                });

            return deferred.promise;
        }

        function updateDevicesPermissions(mic, allowedMicFound, webcam, allowedWebcamFound) {
            logger.debug(
                'updateDevicesPermissions',
                `request system permissions for microphone: ${mic}, allowed mic found: ${allowedMicFound}; webcam: ${webcam}, allowed webcam found: ${allowedWebcamFound}`
            );

            return service
                .requestSystemPermissionForDevices()
                .then(function (permissions) {
                    logger.info('updateDevicesPermissions', 'permissions:', permissions);

                    if (mic) {
                        macOSSystemPermissions.audioAllowed = permissions.audioAllowed;

                        service.setDevicePermissions({
                            audioAllowed: allowedMicFound && permissions.audioAllowed
                        });
                    }

                    if (webcam) {
                        macOSSystemPermissions.videoAllowed = permissions.videoAllowed;

                        service.setDevicePermissions({
                            videoAllowed: allowedWebcamFound && permissions.videoAllowed
                        });
                    }
                    if (!service.isAudioDeviceBlocked() && !service.isVideoDeviceBlocked()) {
                        service.hideBlockedPermissionsBanner();
                    }
                })
                .catch(function (err) {
                    logger.warn('updateDevicesPermissions', 'requestSystemPermissionForDevices error:', err);

                    if (mic) {
                        service.setDevicePermissions({
                            audioAllowed: allowedMicFound
                        });
                    }

                    if (webcam) {
                        service.setDevicePermissions({
                            videoAllowed: allowedWebcamFound
                        });
                    }
                });
        }

        function getCurrentlySelectedMic() {
            var deviceId = avDeviceSettingsService.getUsingAudioIn();
            for (var i = 0; i < service.microphones.length; i++) {
                if (deviceId === service.microphones[i].deviceId) {
                    return service.microphones[i];
                }
            }

            return null;
        }

        function getCurrentlySelectedWebcam() {
            var deviceId = avDeviceSettingsService.getUsingVideo();
            for (var i = 0; i < service.webcams.length; i++) {
                if (deviceId === service.webcams[i].deviceId) {
                    return service.webcams[i];
                }
            }

            return null;
        }

        function checkForBTHeadset() {
            if (!helperService.isWindows()) {
                return;
            }

            logger.debug('checkForBTHeadset', 'Starting');

            //If BT Mic is active, only show BT Headset with matching groupId
            //Hide all other BT Output (even if they aren't the same physical device) -- these devices will not be able to output any sound

            var currentlySelectedMic = service.getCurrentlySelectedMic();
            var btMicGroupId;
            if (currentlySelectedMic && currentlySelectedMic.label.toLowerCase().indexOf('bluetooth') > -1) {
                btMicGroupId = currentlySelectedMic.groupId;
            }

            var matchingBTHeadsetIndex = service.speakers.findIndex(function (speaker) {
                return speaker.groupId === btMicGroupId;
            });

            //groupId is the only way we can guarantee that a BT Speaker is the same physical device as the BT Mic.
            //However, sometimes groupId does NOT match... in which case we should just return and not attempt to filter any BT Speakers.
            if (matchingBTHeadsetIndex === -1) {
                logger.debug(
                    'checkForBTHeadset',
                    'Could not find headset with matching groupId... will not filter BT devices'
                );
                return;
            }

            if (btMicGroupId) {
                //Remove all other BT Headsets
                service.speakers = service.speakers.filter(function (speaker) {
                    return speaker.label.toLowerCase().indexOf('bluetooth') === -1 || speaker.groupId === btMicGroupId;
                });

                //If currentlySelectedSpeaker was removed from list, update to next available speaker
                var selectedSpeaker = service.speakers.find(function (speaker) {
                    return speaker.deviceId === avDeviceSettingsService.getUsingAudioOut();
                });

                if (!selectedSpeaker) {
                    //Ideally, fall back to matching headset
                    var matchingHeadset = service.speakers.find(function (speaker) {
                        return speaker.groupId === btMicGroupId;
                    });

                    var fallbackSpeaker = matchingHeadset || service.speakers[0];
                    if (fallbackSpeaker) {
                        logger.debug(
                            'checkForBTHeadset',
                            'Updating audio output to default:',
                            fallbackSpeaker.deviceId
                        );
                        avDeviceSettingsService.setUsingAudioOut(fallbackSpeaker.deviceId);
                        $rootScope.$broadcast('update-audio-output');
                    }
                }
            }
        }

        function hasMicrophone() {
            return devicesAvailable.microphone;
        }

        function hasSpeaker() {
            return devicesAvailable.speaker;
        }

        function hasCamera() {
            return devicesAvailable.webcam;
        }

        function setHasMicrophone(hasMicrophoneParam) {
            devicesAvailable.microphone = hasMicrophoneParam ? true : false;
        }

        function setHasCamera(hasCameraParam) {
            devicesAvailable.webcam = hasCameraParam ? true : false;
        }

        function getDevicePermissions() {
            return devicePermissions;
        }

        function setDevicePermissions(newDevicePermissions, sendExtended) {
            var devicePermissionsChanged = false;

            if (newDevicePermissions) {
                if (
                    newDevicePermissions.audioAllowed !== undefined &&
                    newDevicePermissions.audioAllowed !== devicePermissions.audioAllowed
                ) {
                    devicePermissions.audioAllowed = newDevicePermissions.audioAllowed;
                    devicePermissionsChanged = true;
                }

                if (
                    newDevicePermissions.videoAllowed !== undefined &&
                    newDevicePermissions.videoAllowed !== devicePermissions.videoAllowed
                ) {
                    devicePermissions.videoAllowed = newDevicePermissions.videoAllowed;
                    devicePermissionsChanged = true;
                }
            }

            if (devicePermissionsChanged) {
                $rootScope.$broadcast('device-permissions-updated', { devicePermissions: devicePermissions });
                service.sendDevicePermissions(sendExtended);
            }
        }

        //TODO; maybe remove this flag and always send the full set
        function sendDevicePermissions(sendExtended) {
            var messageData;
            var devices = {};

            if (sendExtended) {
                try {
                    if (localStreamManagerService.currentLocalAudioTrack) {
                        devices.audioInputDevice = localStreamManagerService.currentLocalAudioTrack.label;
                    }

                    if (localStreamManagerService.currentLocalVideoTrack) {
                        devices.videoInputDevice = localStreamManagerService.currentLocalVideoTrack.label;
                    }
                } catch (err) {
                    logger.error('sendDevicePermissions', 'Error retrieving a/v track labels', err);
                }

                messageData = {
                    message: 'attendee-device-permissions-updated',
                    data: {
                        devicePermissions: service.getDevicePermissions(),
                        devices: devices,
                        saveDevicePermissionsToCDR: true,
                        hasMicrophone: service.hasMicrophone(),
                        hasCamera: service.hasCamera()
                    }
                };
            } else {
                messageData = {
                    message: 'attendee-device-permissions-updated',
                    data: { devicePermissions: service.getDevicePermissions() }
                };
            }

            socketService.send(messageData);
        }

        function getDeviceErrors() {
            return deviceErrors;
        }

        function setDeviceErrors(newDeviceErrors) {
            if (newDeviceErrors) {
                if (newDeviceErrors.camError !== undefined) {
                    deviceErrors.camError = newDeviceErrors.camError;
                    deviceErrors.camAdditional = '';
                    if (newDeviceErrors.camAdditional !== undefined) {
                        deviceErrors.camAdditional = newDeviceErrors.camAdditional;
                    }
                }

                if (newDeviceErrors.micError !== undefined) {
                    deviceErrors.micError = newDeviceErrors.micError;
                    deviceErrors.micAdditional = '';
                    if (newDeviceErrors.micAdditional !== undefined) {
                        deviceErrors.micAdditional = newDeviceErrors.micAdditional;
                    }
                }
            }
        }

        function setShouldIgnoreCamera(newIgnoreCamera) {
            ignoreCamera = newIgnoreCamera;
        }

        function shouldIgnoreCamera() {
            return ignoreCamera;
        }

        function startDevicePermissionsFlow(opts) {
            return service
                .checkIfAVDevicePermissionsGranted()
                .catch(function (err) {
                    logger.debug(
                        'startDevicePermissionsFlow',
                        'Unable to check if AV Device permissions granted:',
                        err
                    );
                    return;
                })
                .then(function () {
                    //If at least one of audio or video device permissions not already granted,
                    //call startWebrtcPromptOverlay which will trigger device permissions prompt
                    if (service.audioOrVideoBlocked()) {
                        return service
                            .startWebrtcPromptOverlay(opts)
                            .catch(function (err) {
                                logger.error('startDevicePermissionsFlow', 'error in startWebrtcPromptOverlay', err);
                            })
                            .then(function () {
                                if (helperService.getChromeVersion() >= 81) {
                                    logger.debug(
                                        'startDevicePermissionsFlow',
                                        'Chrome 81+ detected... checking mic and cam availability'
                                    );
                                    return service.determineMicAndCamAvailability(true).catch(function (err) {
                                        logger.error(
                                            'startDevicePermissionsFlow',
                                            'error in determineMicAndCamAvailability',
                                            err
                                        );
                                    });
                                }
                            });
                    }
                });
        }

        function audioOrVideoBlocked() {
            var devicePermissions = service.getDevicePermissions();
            return !devicePermissions.audioAllowed || !devicePermissions.videoAllowed;
        }

        // show the webrtc permissions prompt by invoking
        // getUserMedia. This returns a promise.
        function startWebrtcPromptOverlay(opts) {
            var deferred = $q.defer();

            if (helperService.isElectron()) {
                deferred.resolve();
                return deferred.promise;
            }

            // this will show the built-in prompt
            service
                .getStream({ audio: service.hasMicrophone(), video: service.hasCamera() })
                .then(function (stream) {
                    logger.debug('startWebrtcPromptOverlay', 'successful');
                    service.cancelDevicePermissionPrompt();

                    var audioTracks = stream.getAudioTracks();
                    if (audioTracks && audioTracks.length > 0) {
                        service.setDevicePermissions({ audioAllowed: true });
                    }

                    var videoTracks = stream.getVideoTracks();
                    if (videoTracks && videoTracks.length > 0) {
                        service.setDevicePermissions({ videoAllowed: true });
                    }

                    stream.getTracks().forEach(function (track) {
                        try {
                            track.stop();
                        } catch (err) {}
                    });
                    deferred.resolve();
                })
                .catch(function (err) {
                    logger.debug('startWebrtcPromptOverlay', 'error:', err);
                    deferred.reject(err);

                    service.cancelDevicePermissionPrompt();
                    if (connectivityService.getIsInDeviceSelectionFlow()) {
                        return;
                    }
                    service.showBlockedPermissionsBanner();

                    if (!opts || !opts.avSettingsModal) {
                        $timeout(function () {
                            service.showBlockedAlert();
                        }, 500);
                    }
                });

            service.showDevicePermissionPromptWithDelay(1000);
            return deferred.promise;
        }

        // following functions were moved from conferenceCtrl to avDeviceService

        function requestSystemPermissionForDevices() {
            var deferred = $q.defer();
            var systemMicrophonePermissionAllowed = true;
            var systemCameraPermissionAllowed = true;
            let navigator = $window.navigator;

            // Refactored from original code, media is one of “audio” or “video”
            function getUserMediaPromise(media) {
                logger.debug('requestSystemPermissionForDevices', 'getUserMediaPromise :', media);

                const constraints = media === 'audio' ? { audio: true } : { video: true };
                // if (!mediaDevices) {
                //   setMediaDevice();
                // }
                // Revert useMediaService code, Ref. ANYAM-114891
                return navigator.mediaDevices
                    .getUserMedia(constraints)
                    .then(function (stream) {
                        stream.getTracks().forEach(function (track) {
                            try {
                                track.stop();
                            } catch (err) {}
                        });
                    })
                    .catch(function (err) {
                        logger.error(
                            'requestSystemPermissionForDevices->getUserMediaPromise',
                            'getUserMedia error',
                            err
                        );
                        sendMsgflowEvent('device-error', {
                            reason: 'getLocalAudioStream',
                            constraints: constraints,
                            error: err?.name
                        });
                        if (err && err.message === 'Permission denied by system') {
                            switch (media) {
                                case 'audio':
                                    systemMicrophonePermissionAllowed = false;
                                    break;
                                case 'video':
                                    systemCameraPermissionAllowed = false;
                                    break;
                            }
                        }
                    });
            }

            if (
                meetingService.getIsSfuEnabled() &&
                (helperService.isChromeOSXCatalina() || helperService.isWindows())
            ) {
                var ms = 3000;

                var getUserMediaTimeout;

                var timeoutPromise = $q(function (resolve) {
                    getUserMediaTimeout = $timeout(resolve, ms);
                }).then(function () {
                    if (getUserMediaTimeout !== null) {
                        throw new Error('getUserMedia calls did not resolve within ' + ms + ' seconds.');
                    }
                });
                var audioPromise, videoPromise;
                if (service.usePermissionsAPI && navigator.permissions && navigator.permissions.query) {
                    logger.debug('requestSystemPermissionForDevices', 'Using permissions API');
                    audioPromise = navigator.permissions
                        .query({ name: 'microphone' })
                        .then(function (permissionStatus) {
                            switch (permissionStatus.state) {
                                case 'granted':
                                    break;
                                case 'prompt':
                                case 'denied':
                                    return getUserMediaPromise('audio');
                            }
                        })
                        .catch(function (err) {
                            return getUserMediaPromise('audio');
                        });
                    videoPromise = navigator.permissions
                        .query({ name: 'camera' })
                        .then(function (permissionStatus) {
                            switch (permissionStatus.state) {
                                case 'granted':
                                    break;
                                case 'prompt':
                                case 'denied':
                                    return getUserMediaPromise('video');
                            }
                        })
                        .catch(function (err) {
                            return getUserMediaPromise('video');
                        });
                } else {
                    logger.debug('requestSystemPermissionForDevices', 'Using getUserMedia API');
                    audioPromise = getUserMediaPromise('audio');

                    // Check video
                    videoPromise = getUserMediaPromise('video');
                }

                var allPromise = $q.allSettled([audioPromise, videoPromise]).catch(angular.noop);

                $q.race([timeoutPromise, allPromise])
                    .then(function (value) {
                        logger.debug('requestSystemPermissionForDevices', 'Permissions:', value);
                    })
                    .catch(function (err) {
                        logger.error('requestSystemPermissionForDevices', 'Error', err);
                    })
                    .finally(function () {
                        deferred.resolve({
                            audioAllowed: systemMicrophonePermissionAllowed,
                            videoAllowed: systemCameraPermissionAllowed
                        });

                        // Cleanup promises and timers
                        $timeout.cancel(getUserMediaTimeout);
                        getUserMediaTimeout = null;
                        timeoutPromise = null;
                        audioPromise = null;
                        videoPromise = null;
                        allPromise = null;
                    });
            } else if (helperService.isMac() && helperService.isElectron()) {
                Promise.all([
                    $window.preloadedApi.systemPreferences.askForMediaAccess('microphone'),
                    $window.preloadedApi.systemPreferences.askForMediaAccess('camera')
                ])
                    .then(([micAllowed, cameraAllowed]) => {
                        deferred.resolve({
                            audioAllowed: micAllowed,
                            videoAllowed: cameraAllowed
                        });
                    })
                    .catch(err => {
                        logger.error(
                            'requestSystemPermissionForDevices',
                            'error acquiring systemPreferences from electron object',
                            err
                        );

                        deferred.resolve({
                            audioAllowed: systemMicrophonePermissionAllowed,
                            videoAllowed: systemCameraPermissionAllowed
                        });
                    });
            } else {
                deferred.resolve({
                    audioAllowed: systemMicrophonePermissionAllowed,
                    videoAllowed: systemCameraPermissionAllowed
                });
            }

            return deferred.promise;
        }

        function checkIfAVDevicePermissionsGranted() {
            var deferred = $q.defer();
            var mediaDevices = helperService.getMediaDevices();

            logger.info('checkIfAVDevicePermissionsGranted', 'Checking if AV device permissions are granted');

            //Devices with allowed permissions will have a label property
            if (mediaDevices && mediaDevices.enumerateDevices) {
                const startTime = Date.now();
                const retryEnumerateDevices = shouldRetryEnumerateDevices();

                const enumerateDevicesPromise = retryEnumerateDevices
                    ? service.enumerateDevicesWithRetries('device-permissions-check')
                    : mediaDevices.enumerateDevices();
                enumerateDevicesPromise
                    .then(function (devices) {
                        const delay = Date.now() - startTime;
                        updateEnumerateDevicesDelay(delay, 'device-permissions:success');
                        var allowedMicFound = false;
                        var allowedWebcamFound = false;

                        // Loop through all devices to find an audio device and a video device that
                        // have device labels (an empty label means the device is blocked by the browser)
                        for (var i = 0; i < devices.length; i++) {
                            if (devices[i].label !== '') {
                                if (devices[i].kind === 'audioinput' && !allowedMicFound) {
                                    allowedMicFound = true;
                                } else if (devices[i].kind === 'videoinput' && !allowedWebcamFound) {
                                    allowedWebcamFound = true;
                                }
                            }

                            if (allowedMicFound && allowedWebcamFound) {
                                break;
                            }
                        }

                        logger.info(
                            `#checkIfAVDevicePermissionsGranted: Audio device permissions initial state: ${
                                allowedMicFound ? 'granted' : 'not granted'
                            }`
                        );
                        logger.info(
                            `#checkIfAVDevicePermissionsGranted: Video device permissions initial state: ${
                                allowedWebcamFound ? 'granted' : 'not granted'
                            }`
                        );

                        updateDevicesPermissions(true, allowedMicFound, true, allowedWebcamFound).then(() =>
                            deferred.resolve()
                        );
                    })
                    .catch(function (err) {
                        const delay = Date.now() - startTime;
                        updateEnumerateDevicesDelay(delay, 'device-permissions:error');
                        deferred.reject(err);
                    });
            } else {
                deferred.reject('enumerateDevices not available');
            }

            return deferred.promise;
        }

        function getMacOSSystemPermissions() {
            return macOSSystemPermissions;
        }

        function showBlockedPermissionsBanner() {
            var supportLink;
            var header = '';
            var content = '';

            if (helperService.isChrome()) {
                supportLink = supportLinkService.getSupportLink('allowMicChrome');
            } else if (helperService.isElectron() && helperService.isMac()) {
                supportLink = supportLinkService.getSupportLink('catalinaScreenShare');
            }
            content =
                supportLink !== 'base_url_not_available' && !helperService.isVideoRoomApplication()
                    ? localizationService.translate('HEADER.NO_PERMISSION_TO_YOUR_MIC_OR_WEBCAM_WITH_URL_TO_FIX', {
                          link: supportLink
                      })
                    : localizationService.translate('HEADER.NO_PERMISSION_TO_YOUR_MIC_OR_WEBCAM');
            $rootScope.$broadcast('banner-message-show', {
                key: 'device-permissions-blocked',
                header: header,
                content: content,
                persist: true
            });
        }

        function hideBlockedPermissionsBanner() {
            $rootScope.$broadcast('banner-message-dismiss', { key: 'device-permissions-blocked', forceDismiss: true });
        }

        function showBlockedAlert() {
            var permissions = service.getDevicePermissions();
            var alertTitle;
            var alertText;

            if (!permissions.audioAllowed) {
                alertTitle = permissions.videoAllowed
                    ? localizationService.translate('HEADER.DIALOG.MICROPHONE_BLOCKED')
                    : localizationService.translate('HEADER.DIALOG.CAMERA_AND_MICROPHONE_BLOCKED');

                alertText = permissions.videoAllowed
                    ? localizationService.translate('HEADER.DIALOG.MICROPHONE_BLOCKED_MESSAGE')
                    : localizationService.translate('HEADER.DIALOG.CAMERA_AND_MICROPHONE_BLOCKED_MESSAGE');
            } else if (!permissions.videoAllowed) {
                alertText = localizationService.translate('HEADER.DIALOG.CAMERA_BLOCKED_MESSAGE');
                alertTitle = localizationService.translate('HEADER.DIALOG.CAMERA_BLOCKED');
            } else {
                //This case can happen if permissions are allowed on Chrome, but blocked at OS level
                //in which case, another alert will be triggered elsewhere
                logger.warn('showBlockedAlert', 'called but no devices are blocked');
                return;
            }

            var okBtn = localizationService.translate('COMMON.OK_FIX_IT');
            var cancelBtn = localizationService.translate('COMMON.CANCEL_GOT_IT');

            if (helperService.getDevicePlatform() === 'iOS') {
                helperService.mobileConfirm(alertText, alertTitle, alertCallback, [okBtn, cancelBtn].join(','));
            } else {
                if (helperService.getIsRedesign() === true) {
                    var closeButtonHtml =
                        '<button class="sweetalert-close-x" onclick="window.swal.close();" tabindex="3"><img src="img/redesign/icons/close-big.svg"></img></button>';
                    $window.swal(
                        {
                            title: closeButtonHtml + alertTitle,
                            text: '<b>' + alertText + '</b>',
                            showCancelButton: true,
                            showConfirmButton: true,
                            confirmButtonText: okBtn,
                            confirmButtonColor: brandingService.getBaseColor(),
                            closeOnConfirm: false,
                            cancelButtonText: cancelBtn,
                            allowOutsideClick: false,
                            html: true,
                            imageUrl: 'img/redesign/modal/pop-up-pic-warning.svg'
                        },
                        alertCallback
                    );
                } else {
                    //TODO: REMOVE POST-REDESIGN
                    $window.swal(
                        {
                            title: alertTitle,
                            text: '<b>' + alertText + '</b>',
                            type: 'warning',
                            showCancelButton: true,
                            showConfirmButton: true,
                            confirmButtonText: okBtn,
                            confirmButtonColor: brandingService.getBaseColor(),
                            closeOnConfirm: false,
                            cancelButtonText: cancelBtn,
                            allowOutsideClick: false,
                            html: true
                        },
                        alertCallback
                    );
                }
            }

            function alertCallback(userResponse) {
                var fixIt = false;

                if (helperService.getDevicePlatform() === 'iOS') {
                    //1 = Yes
                    //2 = No
                    fixIt = userResponse === 1;
                } else {
                    fixIt = userResponse ? true : false;
                }

                if (fixIt) {
                    var supportLink = '';

                    if (helperService.isChrome()) {
                        supportLink = supportLinkService.getSupportLink('allowMicChrome');
                    } else if (helperService.isMobile()) {
                        supportLink = supportLinkService.getSupportLink('micCamMobile');
                    }

                    meetingService.openExternalLink(supportLink);
                }
            }
        }
        function cancelDevicePermissionPrompt() {
            if (devicePermissionPromptTimeout !== null) {
                $timeout.cancel(devicePermissionPromptTimeout);
                devicePermissionPromptTimeout = null;
                $window.swal.close();
                service.setIsPermissionOverlayOpen(false);
            }
        }

        function showDevicePermissionPromptWithDelay(delay) {
            devicePermissionPromptTimeout = $timeout(function () {
                $window.swal({
                    title: localizationService.translate('HEADER.DIALOG.SHARE_MIC_AND_WEBCAM'),
                    type: 'warning',
                    allowOutsideClick: false,
                    closeOnCancel: false,
                    closeOnConfirm: false,
                    customClass: 'initial-webrtc',
                    html: true
                });

                service.setIsPermissionOverlayOpen(true);

                $window.$('.popover').popover('hide');
            }, delay);
        }

        function determineMicAndCamAvailability(initialCheck, deviceChanged) {
            var storedVideo = avDeviceSettingsService.getUsingVideo(),
                storedAudio = avDeviceSettingsService.getUsingAudioIn(),
                storedAudioOut = avDeviceSettingsService.getUsingAudioOut(),
                foundStoredVideo = false,
                foundStoredAudio = false,
                foundStoredAudioOut = false,
                defaultVideo = null,
                defaultAudio = null,
                defaultAudioOut = null;

            var foundECAudioOut = false,
                foundECAudioIn = false,
                foundECVideoIn = false;

            //Intended to store the raw device object
            var ecAudioOutObj, ecAudioInObj, ecVideoInObj;

            let tmpDefaultAudioInputObj, tmpDefaultAudioOutputObj;

            var deferred = $q.defer();

            // When we init the service, we pull devices from the cookie and set in the local properies ... these
            // are then chosen in the call flow.  But first we iterate through the devices and make sure they exist.  if they
            // don't, then we use the first non-default device we found when iterating through devices
            //
            // If 'explicitly chosen' (in list) devices are available, we prioritize those above the rest
            const oldHasCamera = service.hasCamera();
            const reason = `mic-cam-availability:initialCheck=${initialCheck}, deviceChanged=${deviceChanged}`;
            service
                .enumerateDevices(reason)
                .then(function () {
                    logger.debug('determineMicAndCamAvailability', 'in enumerateDevices');

                    // Ref: ANYAM-114176 #1 Check for real change in device list
                    const deviceChanges = {
                        mic: isDeviceListChanged(service.microphones, service.oldMicrophones),
                        speaker: isDeviceListChanged(service.speakers, service.oldSpeakers),
                        webcam: isDeviceListChanged(service.webcams, service.oldWebcams)
                    };

                    // If no real device change, skip the handling
                    if (deviceChanged && !deviceChanges.mic && !deviceChanges.speaker && !deviceChanges.webcam) {
                        logger.info('determineMicAndCamAvailability', 'No device change detected');
                        $rootScope.$broadcast('log-device-change', false); // false means no change
                        deferred.resolve('No device change detected');
                        return;
                    }

                    if (deviceChanges.mic && !devicePermissions.audioAllowed) {
                        updateDevicesPermissions(true, true, false, false);
                    }

                    if (deviceChanges.webcam && !devicePermissions.videoAllowed) {
                        updateDevicesPermissions(false, false, true, true);
                    }

                    // Logging to msgflow
                    logger.info('determineMicAndCamAvailability', 'Device change detected', deviceChanges);
                    $rootScope.$broadcast('log-device-change', deviceChanges);

                    // microphones
                    ecAudioInObj = avDeviceSettingsService.getExplicitlyChosenAudioIn(service.microphones);
                    const selectMicResult = selectAudioDevice(service.microphones, storedAudio, ecAudioInObj);

                    logger.info(
                        '#determineMicAndCamAvailability: Preliminary microphone selection result',
                        selectMicResult
                    );

                    defaultAudio = selectMicResult.selectedDeviceId;
                    foundStoredAudio = selectMicResult.foundStoredDevice;
                    foundECAudioIn = selectMicResult.foundExplicitlyChosenDevice;
                    tmpDefaultAudioInputObj = selectMicResult.defaultDevice;

                    // webcams
                    ecVideoInObj = avDeviceSettingsService.getExplicitlyChosenVideo(service.webcams);
                    const selectWebcamResult = selectVideoDevice(service.webcams, storedVideo, ecVideoInObj);

                    logger.info(
                        '#determineMicAndCamAvailability: Preliminary webcam selection result',
                        selectWebcamResult
                    );

                    defaultVideo = selectWebcamResult.selectedDeviceId;
                    foundECVideoIn = selectWebcamResult.foundExplicitlyChosenDevice;
                    foundStoredVideo = selectWebcamResult.foundStoredDevice;

                    // speakers
                    ecAudioOutObj = avDeviceSettingsService.getExplicitlyChosenAudioOut(service.speakers);
                    const selectSpeakerResult = selectAudioDevice(service.speakers, storedAudioOut, ecAudioOutObj);

                    logger.info(
                        '#determineMicAndCamAvailability: Preliminary speaker selection result',
                        selectSpeakerResult
                    );

                    defaultAudioOut = selectSpeakerResult.selectedDeviceId;
                    foundECAudioOut = selectSpeakerResult.foundExplicitlyChosenDevice;
                    foundStoredAudioOut = selectSpeakerResult.foundStoredDevice;
                    tmpDefaultAudioOutputObj = selectSpeakerResult.defaultDevice;

                    if (deviceChanged) {
                        const newHasCamera = service.hasCamera();

                        // the only webcam was unplugged or plugged in
                        if (oldHasCamera === false && newHasCamera === true) {
                            service.setIsNewVideoDeviceConnected(true);
                        } else if (oldHasCamera === true && newHasCamera === false) {
                            service.setIsNewVideoDeviceConnected(false);
                        }

                        $rootScope.$broadcast('device-change-detected');

                        //Active speaker was disconnected
                        if (!foundStoredAudioOut) {
                            if (defaultAudioOut) {
                                logger.debug(
                                    'determineMicAndCamAvailability',
                                    `enumerateDevices: active speaker was disconnected... falling back to: ${defaultAudioOut}`
                                );
                                avDeviceSettingsService.setUsingAudioOut(defaultAudioOut);
                                $rootScope.$broadcast('update-audio-output');
                            } else {
                                logger.debug(
                                    'determineMicAndCamAvailability',
                                    'enumerateDevices: active speaker was disconnected, but no fallback device available'
                                );
                            }
                        }

                        var shouldShowGrowl = false;

                        if (
                            !connectivityService.getAudioThroughPhone() &&
                            service.isSystemDevice(storedAudio) &&
                            tmpDefaultAudioInputObj &&
                            service.defaultAudioInputObj
                        ) {
                            if (tmpDefaultAudioInputObj.originalLabel !== service.defaultAudioInputObj.originalLabel) {
                                logger.debug('determineMicAndCamAvailability', 'Default Audio Input Changed');
                                localStreamManagerService.stopLocalAudioTrack();
                                service.untrackStartStopTalking(localStreamManagerService.clonedAudioStream);
                                localStreamManagerService.stopClonedAudioStream();

                                $rootScope.$broadcast('finished-media-device-selection', {
                                    newAudioInputDeviceSelected: true
                                });

                                shouldShowGrowl = true;
                            } else {
                                if (!service.getDevicePermissions().audioAllowed) {
                                    service.checkIfAVDevicePermissionsGranted();
                                    $rootScope.$broadcast('audio-input-enabled', service.defaultAudioInputObj.deviceId);
                                }
                            }
                        }

                        if (
                            service.isSystemDevice(storedAudioOut) &&
                            tmpDefaultAudioOutputObj &&
                            service.defaultAudioOutputObj &&
                            tmpDefaultAudioOutputObj.originalLabel !== service.defaultAudioOutputObj.originalLabel
                        ) {
                            logger.debug('determineMicAndCamAvailability', 'Default Audio Output Changed');
                            shouldShowGrowl = true;
                            $rootScope.$broadcast('update-audio-output');
                        }

                        if (shouldShowGrowl) {
                            notificationService.show(
                                localizationService.translate('NOTIFICATIONS.MEETING_DEVICE_CHANGED_TO_MATCH_OS'),
                                'success'
                            );
                        }

                        if (service.getIsPendingDeviceReconnect()) {
                            logger.debug(
                                'determineMicAndCamAvailability',
                                'enumerateDevices: User pending device reconnection...  will not prompt for explicitly chosen devices'
                            );
                        } else {
                            var ecDevicesChanged = {};

                            if (foundECAudioOut && storedAudioOut !== ecAudioOutObj?.deviceId) {
                                logger.debug(
                                    'determineMicAndCamAvailability',
                                    'enumerateDevices: explicitly-chosen audio output device connected'
                                );
                                ecDevicesChanged.audioOutput = ecAudioOutObj;
                            }

                            if (foundECAudioIn && storedAudio !== ecAudioInObj?.deviceId) {
                                logger.debug(
                                    'determineMicAndCamAvailability',
                                    'enumerateDevices: explicitly-chosen audio input device connected'
                                );
                                ecDevicesChanged.audioInput = ecAudioInObj;
                            }

                            if (foundECVideoIn && storedVideo !== ecVideoInObj?.deviceId) {
                                logger.debug(
                                    'determineMicAndCamAvailability',
                                    'enumerateDevices: explicitly-chosen video input device connected'
                                );
                                ecDevicesChanged.videoInput = ecVideoInObj;
                            }

                            if (Object.keys(ecDevicesChanged).length > 0) {
                                service.promptForExplicitlyChosenDevices(ecDevicesChanged);
                            }
                        }
                    } else {
                        if (service.hasMicrophone()) {
                            if (initialCheck && foundECAudioIn) {
                                logger.debug(
                                    'determineMicAndCamAvailability',
                                    'enumerateDevices: using explicitly chosen audio input device:',
                                    ecAudioInObj.deviceId
                                );
                                avDeviceSettingsService.setUsingAudioIn(ecAudioInObj.deviceId);
                            } else if (foundStoredAudio) {
                                logger.debug(
                                    'determineMicAndCamAvailability',
                                    'enumerateDevices: using stored audio device',
                                    storedAudio
                                );
                            } else if (defaultAudio) {
                                logger.debug(
                                    'determineMicAndCamAvailability',
                                    'enumerateDevices: setting default audio device',
                                    defaultAudio
                                );
                                avDeviceSettingsService.setUsingAudioIn(defaultAudio);
                            } else {
                                logger.debug(
                                    'determineMicAndCamAvailability',
                                    'enumerateDevices: audio device found, but no device id available'
                                );
                            }
                        }

                        if (service.hasCamera()) {
                            if (initialCheck && foundECVideoIn) {
                                logger.debug(
                                    'determineMicAndCamAvailability',
                                    'enumerateDevices: using explicitly chosen video input device:',
                                    ecVideoInObj?.deviceId
                                );
                                avDeviceSettingsService.setUsingVideo(ecVideoInObj?.deviceId);
                            } else if (foundStoredVideo) {
                                logger.debug(
                                    'determineMicAndCamAvailability',
                                    'enumerateDevices: using stored video device',
                                    storedVideo
                                );
                            } else if (defaultVideo) {
                                logger.debug(
                                    'determineMicAndCamAvailability',
                                    'enumerateDevices: setting default video device',
                                    defaultVideo
                                );
                                avDeviceSettingsService.setUsingVideo(defaultVideo);
                            } else {
                                logger.debug(
                                    'determineMicAndCamAvailability',
                                    'enumerateDevices: video device found, but no device id available'
                                );
                            }
                        }

                        if (service.hasSpeaker()) {
                            if (initialCheck && foundECAudioOut) {
                                logger.debug(
                                    'determineMicAndCamAvailability',
                                    'enumerateDevices: using explicitly chosen audio output device:',
                                    ecAudioOutObj?.deviceId
                                );
                                avDeviceSettingsService.setUsingAudioOut(ecAudioOutObj?.deviceId);
                            } else if (foundStoredAudioOut) {
                                logger.debug(
                                    'determineMicAndCamAvailability',
                                    'enumerateDevices: using stored audio output device',
                                    storedAudioOut
                                );
                            } else if (defaultAudioOut) {
                                logger.debug(
                                    'determineMicAndCamAvailability',
                                    'enumerateDevices: setting default audio output device',
                                    defaultAudioOut
                                );
                                avDeviceSettingsService.setUsingAudioOut(defaultAudioOut);
                            } else {
                                logger.debug(
                                    'determineMicAndCamAvailability',
                                    'enumerateDevices: audio output device found, but no device id available'
                                );
                            }
                        }
                    }

                    if (tmpDefaultAudioInputObj) {
                        service.defaultAudioInputObj = tmpDefaultAudioInputObj;
                    }

                    if (tmpDefaultAudioOutputObj) {
                        service.defaultAudioOutputObj = tmpDefaultAudioOutputObj;
                    }

                    if (initialCheck) {
                        logger.debug(
                            `#determineMicAndCamAvailability: enumerateDevices: Initial device check - microphone found: ${service.hasMicrophone()}`
                        );
                        logger.debug(
                            `#determineMicAndCamAvailability: enumerateDevices: Initial device check - webcam found: ${service.hasCamera()}`
                        );
                        logger.debug(
                            `#determineMicAndCamAvailability: enumerateDevices: Initial device check - speaker found: ${service.hasSpeaker()}`
                        );
                    }

                    deferred.resolve();
                })
                .catch(function (err) {
                    logger.error('determineMicAndCamAvailability', 'error in enumerateDevices', err);

                    deferred.reject(err);
                });

            return deferred.promise;
        }

        function selectAudioDevice(devices, storedDeviceId, explicitlyChosenDevice) {
            // keep system device and order them alphabeically, select the first one
            // 'communication' device is higher priority device
            const defaultDevice = devices
                ?.filter(
                    d =>
                        meetingService.canUseSystemMediaDevice() &&
                        (d.deviceId === 'default' || d.deviceId === 'communications')
                )
                .sort((d1, d2) => (d1.deviceId > d2.deviceId ? 1 : -1))[0];

            if (explicitlyChosenDevice) {
                return {
                    foundStoredDevice: explicitlyChosenDevice.deviceId === storedDeviceId,
                    foundExplicitlyChosenDevice: true,
                    selectedDeviceId: explicitlyChosenDevice.deviceId,
                    defaultDevice
                };
            }

            const result = {
                foundStoredDevice: false,
                foundExplicitlyChosenDevice: false,
                selectedDeviceId: undefined,
                defaultDevice
            };

            if (!devices || devices.length === 0) {
                return result;
            }

            result.selectedDeviceId = defaultDevice?.deviceId ?? devices[0].deviceId;
            result.foundStoredDevice = devices.some(d => d.deviceId === storedDeviceId);

            return result;
        }

        function selectVideoDevice(devices, storedDeviceId, explicitlyChosenDevice) {
            if (explicitlyChosenDevice) {
                return {
                    foundStoredDevice: explicitlyChosenDevice.deviceId === storedDeviceId,
                    foundExplicitlyChosenDevice: true,
                    selectedDeviceId: explicitlyChosenDevice.deviceId
                };
            }

            const result = {
                foundStoredDevice: false,
                foundExplicitlyChosenDevice: false,
                selectedDeviceId: undefined
            };

            if (!devices || devices.length === 0) {
                return result;
            }

            for (let j = 0; j < devices.length; j++) {
                if (helperService.getDevicePlatform() === 'iOS' && devices[j].label === 'Back Camera') {
                    logger.debug('selectVideoDevice', 'Skipping Back Camera...');
                    continue;
                }

                if (
                    service.ignoredDevices.webcams.default &&
                    service.ignoredDevices.webcams.default.groupId === devices[j].groupId
                ) {
                    //Prioritize 'default' device first
                    result.selectedDeviceId = devices[j].deviceId;
                } else if (
                    !result.selectedDeviceId &&
                    !meetingService.canUseSystemMediaDevice() &&
                    service.ignoredDevices.webcams.communications &&
                    service.ignoredDevices.webcams.communications.groupId === service.webcams[j].groupId
                ) {
                    //Prioritize 'communications' device second
                    result.selectedDeviceId = service.webcams[j].deviceId;
                }

                if (devices[j].deviceId === storedDeviceId) {
                    result.foundStoredDevice = true;
                }
            }

            if (!result.selectedDeviceId) {
                result.selectedDeviceId = devices[0].deviceId;
            }

            return result;
        }

        function compareDevices(first, second) {
            const firstIsSystem = service.isSystemDevice(first.deviceId) ? 1 : 0;
            const secondIsSystem = service.isSystemDevice(second.deviceId) ? 1 : 0;

            if (service.isDeviceInUse(first) && firstIsSystem === 0) {
                return -1;
            }
            if (service.isDeviceInUse(second) && secondIsSystem === 0) {
                return 1;
            }

            // place system device in the bottom of list
            return firstIsSystem - secondIsSystem;
        }

        function getIsNewVideoDeviceConnected() {
            return newVideoDeviceConnected;
        }

        function setIsNewVideoDeviceConnected(newValue) {
            newVideoDeviceConnected = newValue;
        }

        function hasMicrophoneDeviceError() {
            return (
                connectivityService.getConferenceConnected() &&
                (!service.getDevicePermissions().audioAllowed || service.isMicrophoneHavingAnIssue()) &&
                !connectivityService.getAudioThroughPhone() &&
                service.hasMicrophone()
            );
        }

        function isMicrophoneHavingAnIssue() {
            if (helperService.getDevicePlatform() === 'iOS') {
                return !service.getDevicePermissions().audioAllowed;
            }

            //We intentionally end the audio track if switched-to-phone
            if (connectivityService.getAudioThroughPhone()) {
                return false;
            }

            if (service.getIsMicrophoneDeviceSwitching()) {
                return false;
            }

            if (
                service.getSfuDeviceErrorFromId(avDeviceSettingsService.getUsingAudioIn()) &&
                !localStreamManagerService.isLocalAudioTrackActive()
            ) {
                return true;
            }

            return service.getDevicePermissions().audioAllowed && !localStreamManagerService.isLocalAudioTrackActive();
        }

        function isAudioDeviceBlocked() {
            var permissions = service.getDevicePermissions();
            return service.hasMicrophone() && permissions && !permissions.audioAllowed;
        }

        function hasWebcamDeviceError() {
            return (
                (service.isVideoDeviceBlocked() &&
                    service.hasCamera() &&
                    connectivityService.getConferenceConnected()) ||
                service.isWebcamHavingAnIssue()
            );
        }

        function isWebcamHavingAnIssue() {
            return !!service.getSfuDeviceErrorFromId(avDeviceSettingsService.getUsingVideo());
        }

        function isVideoDeviceBlocked() {
            var permissions = service.getDevicePermissions();
            return service.hasCamera() && permissions && !permissions.videoAllowed;
        }

        function setIsMicrophoneDeviceSwitching(newMicrophoneDeviceSwitching) {
            logger.debug('setIsMicrophoneDeviceSwitching', 'setIsMicrophoneDeviceSwitching ...');
            microphoneDeviceSwitching = newMicrophoneDeviceSwitching;
        }

        function getIsMicrophoneDeviceSwitching() {
            return microphoneDeviceSwitching;
        }

        function initDeviceChangeListener() {
            if ($window.navigator && $window.navigator.mediaDevices) {
                if (!$window.navigator.mediaDevices.ondevicechange) {
                    logger.debug('initDeviceChangeListener', 'Initializing ondevicechange listener');
                    $window.navigator.mediaDevices.ondevicechange = service.onDeviceChange;
                } else {
                    logger.info('initDeviceChangeListener', 'ondevicechange already initialized');
                }
            } else {
                logger.warn(
                    'initDeviceChangeListener',
                    'Invalid mediaDevices... unable to init ondevicechange listener'
                );
            }
        }

        function removeDeviceChangeListener() {
            if ($window.navigator.mediaDevices.ondevicechange) {
                logger.debug('removeDeviceChangeListener', 'Removing ondevicechange listener');
                $window.navigator.mediaDevices.ondevicechange = undefined;
            }
        }

        function onDeviceChange() {
            logger.info('onDeviceChange', 'ondevicechange triggered');
            // This event does not contain any detail about which devices were added/removed...
            // So we need to call enumerateDevices (via determineMicAndCamAvailability) to determine the device changes

            // In some cases, a single physical device will have a mic AND webcam, or mic AND speaker, etc.
            // This will trigger multiple events usually within a couple of hundred milliseconds of each other (hence the service.deviceChangeConfig.delay=2000ms timeout)
            // We want enumerateDevices to be called after the timeout because if multiple devices are unplugged at once,
            // It will return the latest/most accurate list of devices

            let config = service.deviceChangeConfig;
            // Ref: ANYAM-114176 #1 Collate quick events and act as one
            if (config.inProgress || config.delayTimer) {
                logger.info('onDeviceChange', 'ondevicechange triggered instantly');

                const event = config.inProgress ? 'in-progress' : 'in-timer';
                $rootScope.$broadcast('log-device-change', event);
                logger.info('onDeviceChange', 'Last handling of ondevicechange is still ' + event);
                // Ref: ANYAM-114176 #1 Cancel and restart the delay timer so rapid device-change events are collated
                if (config.delayTimer) {
                    $timeout.cancel(config.delayTimer);
                    config.delayTimer = null;
                } else {
                    config.isPending = true;
                    return;
                }
            }
            // Set a delay timer to collate quick events and act as one
            config.delayTimer = $timeout(function () {
                config.inProgress = true;
                config.delayTimer = null;
                if (helperService.getVDIRef().handleDetachedTrackOnDeviceChange) {
                    service.handleDetachedDeviceTrackOnVDI();
                }

                if (connectivityService.getIsInDeviceSelectionFlow()) {
                    $rootScope.$broadcast('device-changed-during-device-preview');
                    config.inProgress = false;
                    handlePendingOnDeviceChangeIfAny();
                } else {
                    service.determineMicAndCamAvailability(false, true).finally(() => {
                        config.inProgress = false;
                        handlePendingOnDeviceChangeIfAny();
                    });
                }
            }, config.delay);

            if (config.webcamTimer) {
                $timeout.cancel(config.webcamTimer);
                config.webcamTimer = null;
            }
            config.webcamTimer = $timeout(function () {
                if (
                    (helperService.getVDIRef().getVdiType() === executionEnvironmentEnum.Citrix ||
                        helperService.getVDIRef().getVdiType() === executionEnvironmentEnum.VMware) &&
                    !attendeeService.getIsVideoMuted() &&
                    !attendeeService.getIsVideoActive()
                ) {
                    config.webcamTimer = null;
                    // VDI - Fallback to internal webcam if active webcam device is detached.
                    sendMsgflowEvent('track-ended:video', { reason: 'VDI webcam timeout' });
                    $rootScope.$broadcast(
                        'active-track-ended',
                        localStreamManagerService.getCurrentWebcamStreamForVDI().getTracks()[0]
                    );
                }
            }, config.webcamDelay);
        }

        // This function handles the `ondevicechange` event triggered after a delay specified by config.delay (2000 ms)
        // and before the previous handling is completed.
        // The `isPending` flag is set only if no previous timeout exists and `inProgress` is set,
        // indicating that a subsequent event occurred after the 2000 ms delay.
        function handlePendingOnDeviceChangeIfAny() {
            let config = service.deviceChangeConfig;
            if (!config.inProgress && config.isPending) {
                logger.info(
                    'handlePendingOnDeviceChangeIfAny',
                    'Last ondevicechange handling is completed. Processing pending ondevicechange event.'
                );
                config.isPending = false;
                service.onDeviceChange();
            }
        }

        function promptForExplicitlyChosenDevices(devices) {
            if (!devices || (!devices.audioInput && !devices.audioOutput && !devices.videoInput)) {
                logger.debug(
                    'promptForExplicitlyChosenDevices',
                    'Returning from promptForExplicitlyChosenDevices due to invalid params'
                );
                return;
            }

            var closeButtonHtml =
                '<button class="sweetalert-close-x" onclick="window.swal.close();" tabindex="3"><img src="img/redesign/icons/close-big.svg"></img></button>';

            var text = '';

            if (devices.audioInput) {
                text += '<br/>' + localizationService.translate('COMMON.MICROPHONE') + ': ' + devices.audioInput.label;
            }

            if (devices.audioOutput) {
                text += '<br/>' + localizationService.translate('COMMON.SPEAKER') + ': ' + devices.audioOutput.label;
            }

            if (devices.videoInput) {
                text += '<br/>' + localizationService.translate('COMMON.WEBCAM') + ': ' + devices.videoInput.label;
            }

            text +=
                '<br/><br/><span class="bold">' +
                localizationService.translate('HEADER.DIALOG.NEW_DEVICES_DETECTED_TEXT') +
                '</span>';

            logger.debug('promptForExplicitlyChosenDevices', 'Prompting user to switch to explicitly-chosen devices');

            $window.swal(
                {
                    title: closeButtonHtml + localizationService.translate('HEADER.DIALOG.NEW_DEVICES_DETECTED_TITLE'),
                    text: text,
                    showCancelButton: true,
                    showConfirmButton: true,
                    confirmButtonText: localizationService.translate('COMMON.YES_TEXT'),
                    confirmButtonColor: brandingService.getBaseColor(),
                    closeOnConfirm: true,
                    cancelButtonText: localizationService.translate('COMMON.NO_TEXT'),
                    allowOutsideClick: false,
                    html: true,
                    imageUrl: 'img/redesign/modal/pop-up-pic-warning.svg'
                },
                function (switchToECD) {
                    if (switchToECD) {
                        logger.debug(
                            'promptForExplicitlyChosenDevices',
                            'User chose to switch to explicitly-chosen devices'
                        );

                        if (devices.audioInput) {
                            avDeviceSettingsService.setUsingAudioIn(devices.audioInput.deviceId);
                        }

                        if (devices.audioOutput) {
                            avDeviceSettingsService.setUsingAudioOut(devices.audioOutput.deviceId);
                        }

                        if (devices.videoInput) {
                            avDeviceSettingsService.setUsingVideo(devices.videoInput.deviceId);
                        }

                        //This will restart the Janus media with updated devices
                        $rootScope.$broadcast('finished-media-device-selection', {
                            newVideoInputDeviceSelected: !!devices.videoInput,
                            newAudioInputDeviceSelected: !!devices.audioInput,
                            newAudioOutputDeviceSelected: !!devices.audioOutput,
                            fromExplicitDevicePrompt: true
                        });
                        if (
                            helperService.getVDIRef().getVdiType() === executionEnvironmentEnum.Citrix ||
                            helperService.getVDIRef().getVdiType() === executionEnvironmentEnum.VMware
                        ) {
                            service.switchToSelectedAudioInputOnVdi();
                        }
                    } else {
                        logger.debug(
                            'promptForExplicitlyChosenDevices',
                            'User chose not to switch to explicitly-chosen devices'
                        );
                    }
                }
            );
        }

        function setIsPendingDeviceReconnect(newPendingDeviceReconnect) {
            pendingDeviceReconnect = newPendingDeviceReconnect;
        }

        function getIsPendingDeviceReconnect() {
            return pendingDeviceReconnect;
        }

        function setSfuDeviceError(deviceId, errorName) {
            logger.debug('setSfuDeviceError', `setSfuDeviceError ... ${deviceId} ${errorName}`);
            if (!deviceId) {
                logger.debug('setSfuDeviceError', 'Unable to set device error - invalid deviceId');
                return;
            }

            if (!errorName) {
                errorName = 'Unknown Error';
            }

            logger.debug('setSfuDeviceError', `Setting device error - ${deviceId} - ${errorName}`);
            sfuDeviceErrors[deviceId] = errorName;
            $rootScope.$broadcast('sfu-device-errors-changed');
        }

        function clearSfuDeviceError(deviceId) {
            if (!deviceId) {
                logger.debug('clearSfuDeviceError', 'Unable to clear device error - invalid deviceId');
                return;
            }

            if (sfuDeviceErrors[deviceId]) {
                logger.debug('clearSfuDeviceError', `Clearing device error - ${deviceId}`);
                delete sfuDeviceErrors[deviceId];
                $rootScope.$broadcast('sfu-device-errors-changed');
            } else {
                logger.debug(
                    'clearSfuDeviceError',
                    'Unable to clear device error - error does not exist in deviceErrors obj'
                );
            }
        }

        function getSfuDeviceErrorFromId(deviceId) {
            if (sfuDeviceErrors && sfuDeviceErrors[deviceId]) {
                return sfuDeviceErrors[deviceId];
            } else {
                return null;
            }
        }

        function getSfuDeviceErrors() {
            return sfuDeviceErrors;
        }

        function openMicCamPermissionTroubleShootPage() {
            var kbArticle = supportLinkService.getSupportLink('allowMicChrome');
            $window.open(kbArticle, '_blank');
        }

        function openMicCamPermissionTroubleShootPageForElectron() {
            var kbArticle = supportLinkService.getSupportLink('catalinaScreenShare');
            $window.open(kbArticle, '_blank');
        }

        function openCameraSystemPreference() {
            macSecurityPreferencesService.openCamera();
        }

        function openMicrophoneSystemPreference() {
            macSecurityPreferencesService.openMicrophone();
        }

        function setIsPermissionOverlayOpen(newVal) {
            isPermissionOverlayOpen = newVal;
        }

        function getIsPermissionOverlayOpen() {
            return isPermissionOverlayOpen;
        }

        function isAudioDisconnected() {
            return audioDisconnected;
        }

        function setAudioDisconnected(disconnected) {
            audioDisconnected = disconnected;
            var msg = {};
            msg.message = 'audio-disconnect-state-update';
            msg.data = audioDisconnected;
            socketService.send(msg);
        }

        function toggleMuteAudioInputAndOutput() {
            service.setAudioDisconnected(!audioDisconnected);
            storageService.set(
                'disconnected-audio',
                JSON.stringify({ disconnected: audioDisconnected, meetingCode: meetingService.getMeetingCode() })
            );
            connectivityService.disconnectAudioInputAndOutput(audioDisconnected);
        }

        function handleDetachedDeviceTrackOnVDI() {
            let disconnectedMic = service.getCurrentlySelectedMic()?.label;
            service
                .getActiveDevices()
                .then(function (activeDevices) {
                    var activeAudioInput =
                        activeDevices && activeDevices.audioInput ? activeDevices.audioInput.label : '';
                    if (!activeAudioInput && disconnectedMic !== undefined) {
                        avDeviceSettingsService.setUsingAudioIn('default');
                        let fallbackMic = service.getCurrentlySelectedMic()?.label;
                        if (fallbackMic === undefined) {
                            fallbackMic = localizationService.translate('HEADER.SETTINGS.OS_SETTINGS');
                        }
                        $window.swal({
                            title: localizationService.translate('HEADER.DIALOG.ACTIVE_MIC_SWITCH_TITLE'),
                            text: localizationService.translate('HEADER.DIALOG.ACTIVE_MIC_SWITCH_MESSAGE', {
                                disconnectedMic,
                                fallbackMic
                            }),
                            type: 'warning',
                            showCancelButton: false,
                            showConfirmButton: true,
                            confirmButtonColor: brandingService.getBaseColor(),
                            confirmButtonText: localizationService.translate('COMMON.OKAY_TEXT'),
                            allowOutsideClick: false,
                            closeOnConfirm: true,
                            html: true
                        });
                    }
                })
                .catch(function (err) {
                    logger.error('handleDetachedDeviceTrackOnVDI', 'Unable to retrieve active devices', err);
                });
        }

        function switchToSelectedAudioInputOnVdi() {
            let audioOnlyConstraints = service.generateConstraints('audio', avDeviceSettingsService.getUsingAudioIn());
            service
                .getStream(audioOnlyConstraints)
                .then(function (audioOnlyStream) {
                    logger.debug(
                        'switchToSelectedAudioInputOnVdi',
                        'Successully retrieved audio-only stream after device switch'
                    );
                })
                .catch(function (audioOnlyStreamRetrievalError) {
                    logger.debug(
                        'switchToSelectedAudioInputOnVdi',
                        'Error retrieving audio-only stream after device switch',
                        audioOnlyStreamRetrievalError
                    );
                });
        }

        function getAllPhysicalMicDevices() {
            return service.microphones.filter(m => !service.isSystemDevice(m.deviceId));
        }
    }
]);
